import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getBranch, getBranches } from "../../api/branches";
import { getShiftHoursConfig } from "../../api/attendance";
import {
  approveTimesheet,
  assignInternalControlWatches,
  cancelTimesheet,
  createTimesheet,
  deleteTimesheet,
  getBranchTimesheetEmployees,
  listTimesheets,
  rejectTimesheet,
  submitTimesheet,
  updateTimesheet,
} from "../../api/timesheet";
import {
  canUserApproveTimesheetRequest,
  mapTimesheetToPendingRequest,
} from "../../utils/dataMappers";
import { isHrFamilyRole } from "../../utils/hrStaffHelpers";
import { employeeMatchesSearch } from "../../utils/employeeSearch";
import { cairoCalendarDateKey } from "../../utils/attendanceTimeFormat";
import { isModeratorTimesheetLeaderUser } from "../../utils/orgHelpers";
import "bootstrap/dist/css/bootstrap.rtl.min.css";
import "./BranchTimesheet.css";

const DAY_LABELS = [
  "السبت",
  "الأحد",
  "الإثنين",
  "الثلاثاء",
  "الأربعاء",
  "الخميس",
  "الجمعة",
];

const STATUS_BADGE = {
  DRAFT: "bg-secondary",
  PENDING: "bg-warning text-dark",
  APPROVED: "bg-success",
  REJECTED: "bg-danger",
  CANCELLED: "bg-dark",
};

const STATUS_LABELS = {
  DRAFT: "مسودة",
  PENDING: "بانتظار الاعتماد",
  APPROVED: "معتمد",
  REJECTED: "مرفوض",
  CANCELLED: "ملغي",
};

const SHIFT_LABELS = {
  NIGHT: "00:00–08:00",
  MORNING: "صباحي",
  EVENING: "مسائي",
  BOTH: "يوم كامل",
  INVENTORY: "جرد",
};

const MODERATOR_SHIFT_WINDOWS = {
  NIGHT: { start: "00:00", end: "08:00", label: "00:00–08:00" },
  MORNING: { start: "08:00", end: "16:00", label: "08:00–16:00" },
  EVENING: { start: "16:00", end: "00:00", label: "16:00–00:00" },
};

/** Sat–Wed system defaults when HR has not set branch hours (OPS) */
const SYSTEM_SHIFT_HOURS = {
  MORNING: {
    default: { start: "11:00", end: "20:00" },
    thuFri: { start: "11:00", end: "21:00" },
  },
  EVENING: {
    default: { start: "15:00", end: "00:00" },
    thuFri: { start: "15:00", end: "01:00" },
  },
};

/** Cashier schedule defaults (independent of OPS branch hours) */
const SYSTEM_CASHIER_SHIFT_HOURS = {
  MORNING: {
    default: { start: "11:00", end: "20:00" },
    thuFri: { start: "11:00", end: "21:00" },
  },
  EVENING: {
    default: { start: "15:00", end: "00:00" },
    thuFri: { start: "15:00", end: "01:00" },
  },
};

function isThuOrFriDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return false;
  const match = dateStr.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return false;
  const day = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  ).getUTCDay();
  return day === 4 || day === 5;
}

function getCustomPresetHours(preset, dateStr) {
  if (!preset) return null;
  const block =
    dateStr && isThuOrFriDate(dateStr) ? preset.thuFri : preset.default;
  if (!block?.start || !block?.end) return null;
  return { start: block.start, end: block.end };
}

function resolveShiftHoursConfig(raw) {
  if (
    raw?.MORNING?.default?.start &&
    raw?.MORNING?.default?.end &&
    raw?.MORNING?.thuFri?.start &&
    raw?.MORNING?.thuFri?.end &&
    raw?.EVENING?.default?.start &&
    raw?.EVENING?.default?.end &&
    raw?.EVENING?.thuFri?.start &&
    raw?.EVENING?.thuFri?.end
  ) {
    return raw;
  }
  return SYSTEM_SHIFT_HOURS;
}

function getShiftHours(shift, dateStr, branchHours) {
  const config = resolveShiftHoursConfig(branchHours);
  if (shift === "INVENTORY") {
    return { start: "00:00", end: "23:59" };
  }
  if (shift === "NIGHT") {
    return { start: "00:00", end: "08:00" };
  }
  if (shift === "BOTH") {
    const morningKey = isThuOrFriDate(dateStr) ? "thuFri" : "default";
    const eveningKey = morningKey;
    return {
      start: config.MORNING[morningKey].start,
      end: config.EVENING[eveningKey].end,
    };
  }
  const key = shift === "EVENING" ? "EVENING" : "MORNING";
  const block = config[key];
  if (dateStr && isThuOrFriDate(dateStr)) {
    return { start: block.thuFri.start, end: block.thuFri.end };
  }
  return { start: block.default.start, end: block.default.end };
}

function toDisplayHour(hhmm) {
  return normalizeHhMm(hhmm) || "?";
}

function shiftOptionLabel(shift, branchHours) {
  const hours = getShiftHours(shift, null, branchHours);
  const label = SHIFT_LABELS[shift] || shift;
  return `${label} (${toDisplayHour(hours.start)}–${toDisplayHour(hours.end)})`;
}

function isModeratorTimesheetCreator(user) {
  return isModeratorTimesheetLeaderUser(user);
}

function isMarketingTimesheetCreator(user) {
  if (!user || isModeratorTimesheetCreator(user)) return false;
  const isManager = user.backendRoles?.includes("manager");
  const isMkt =
    user.departmentId === "marketing" ||
    user.department === "التسويق" ||
    user.departmentCode === "MKT";
  return Boolean(isManager && isMkt);
}

function isWarehouseDepartment(user) {
  if (!user) return false;
  if (user.departmentId === "warehouse" || user.departmentCode === "WH") {
    return true;
  }
  return /المخازن/u.test(String(user.department || ""));
}

/** أمين المخزن: manager under warehouse dept, no field branch */
function isWarehouseTimesheetCreator(user) {
  if (!user) return false;
  const isSectionManager = user.backendRoles?.includes("manager");
  const noBranch = !user.branchId;
  return Boolean(isSectionManager && isWarehouseDepartment(user) && noBranch);
}

/** مدير إدارة الرقابة الداخلية — خطة كاميرات الفروع */
function isInternalControlDepartment(user) {
  if (!user) return false;
  if (user.departmentId === "internal-control" || user.departmentCode === "IC") {
    return true;
  }
  const name = String(user.department || "");
  return /الرقاب[ةه]\s*الداخل/u.test(name);
}

function isInternalControlTimesheetCreator(user) {
  if (!user) return false;
  return Boolean(
    user.backendRoles?.includes("department_manager") &&
      isInternalControlDepartment(user),
  );
}

/** أي عضو في الرقابة الداخلية يرى خطط كل الفروع */
function isInternalControlDepartmentMember(user) {
  return isInternalControlDepartment(user);
}

function defaultWarehouseSchedule() {
  return {
    shift: "MORNING",
    scheduledStartTime: "09:00",
    scheduledEndTime: "19:00",
    hasBreak: false,
  };
}

function defaultModeratorSchedule() {
  return {
    shift: "MORNING",
    scheduledStartTime: "08:00",
    scheduledEndTime: "16:00",
    hasBreak: false,
  };
}

function getModeratorShiftHours(shift) {
  return MODERATOR_SHIFT_WINDOWS[shift] || MODERATOR_SHIFT_WINDOWS.MORNING;
}

function warehouseBreakFields(hasBreak) {
  return hasBreak
    ? { expectedBreakHours: 1, expectedPunchCount: 4 }
    : { expectedBreakHours: 0, expectedPunchCount: 2 };
}

function defaultInternalControlSchedule() {
  return {
    shift: "MORNING",
    scheduledStartTime: "09:00",
    scheduledEndTime: "17:00",
    coverageDate: "",
    coverageDates: [],
    workHalfPeriod: "",
  };
}

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function normalizeHhMm(value) {
  if (!value || typeof value !== "string") return value;
  const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return value;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return value;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function isValidHhMm(value) {
  const normalized = normalizeHhMm(value);
  return (
    typeof normalized === "string" &&
    /^([01]\d|2[0-3]):[0-5]\d$/.test(normalized)
  );
}

/** Always shows 24h HH:mm — avoids OS/browser AM/PM time pickers. */
function HhMmInput({
  value,
  defaultValue,
  onChange,
  onBlur,
  id,
  disabled,
  "aria-label": ariaLabel,
  className = "form-control form-control-sm timesheet-hhmm-input",
}) {
  const controlled = value !== undefined;

  function commitNormalized(raw, apply) {
    const normalized = normalizeHhMm(raw);
    if (isValidHhMm(normalized) && raw !== normalized) {
      apply(normalized);
    }
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      placeholder="HH:mm"
      pattern="([01]\d|2[0-3]):[0-5]\d"
      title="صيغة 24 ساعة مثل 17:00"
      maxLength={5}
      dir="ltr"
      className={className}
      id={id}
      disabled={disabled}
      aria-label={ariaLabel}
      {...(controlled
        ? {
            value,
            onChange: (e) => onChange?.(e),
            onBlur: (e) => {
              commitNormalized(e.target.value, (normalized) =>
                onChange?.({ target: { value: normalized } }),
              );
              onBlur?.(e);
            },
          }
        : {
            defaultValue,
            onBlur: (e) => {
              commitNormalized(e.target.value, (normalized) => {
                e.target.value = normalized;
              });
              onBlur?.(e);
            },
          })}
    />
  );
}

function toInputDate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Sat–Fri week containing `dateStr` (YYYY-MM-DD), matching backend UTC week. */
function getWeekRangeContainingIso(dateStr) {
  const match = String(dateStr || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return { weekStart: "", weekEnd: "" };
  const y = Number(match[1]);
  const m = Number(match[2]) - 1;
  const d = Number(match[3]);
  const utc = new Date(Date.UTC(y, m, d));
  const day = utc.getUTCDay();
  const daysSinceSaturday = (day + 1) % 7;
  const weekStart = new Date(utc);
  weekStart.setUTCDate(utc.getUTCDate() - daysSinceSaturday);
  const weekEnd = new Date(weekStart);
  weekEnd.setUTCDate(weekStart.getUTCDate() + 6);
  const toIso = (dt) => {
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(dt.getUTCDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };
  return { weekStart: toIso(weekStart), weekEnd: toIso(weekEnd) };
}

function addUtcDays(isoDate, days) {
  const match = String(isoDate || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return isoDate;
  const dt = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  dt.setUTCDate(dt.getUTCDate() + days);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isActiveTimesheetStatus(status) {
  return status === "DRAFT" || status === "PENDING" || status === "APPROVED";
}

const MAX_WEEKLY_REST_DAYS = 4;
const MAX_MONTHLY_REST_DAYS = 4;
const WEEKLY_REST_QUOTA_NOTE =
  "الراحة الأسبوعية 4 أيام في الأسبوع تتحسب راحة عادي، والحد الشهري 4 أيام فقط في التايم شيت (ما لم تخصّص الموارد البشرية حداً مختلفاً).";

function monthlyRestCapForEmployee(employee, monthKey) {
  const byMonth = employee?.monthlyRestQuotas?.[monthKey];
  if (Number.isFinite(byMonth) && byMonth >= 0) return byMonth;
  if (Number.isFinite(employee?.monthlyRestQuota) && employee.monthlyRestQuota >= 0) {
    return employee.monthlyRestQuota;
  }
  return MAX_MONTHLY_REST_DAYS;
}

function countableRestDateKeys(employee) {
  if (!employee || employee.isCoverage) return [];
  return (employee.entries || [])
    .filter((entry) => entry.dayType === "REST")
    .map((entry) => String(entry.date).slice(0, 10));
}

function otherMonthRestDateKeys(
  timesheets,
  employeeId,
  excludeTimesheetId,
  monthKey,
) {
  const keys = new Set();
  for (const sheet of timesheets || []) {
    if (sheet.id === excludeTimesheetId) continue;
    if (!isActiveTimesheetStatus(sheet.status)) continue;
    if (sheet.scope === "INTERNAL_CONTROL") continue;
    const emp = (sheet.employees || []).find(
      (row) => row.employeeId === employeeId,
    );
    if (!emp || emp.isCoverage) continue;
    for (const dateKey of countableRestDateKeys(emp)) {
      if (dateKey.slice(0, 7) === monthKey) keys.add(dateKey);
    }
  }
  return keys;
}

function monthRestCountForEmployee(employee, timesheets, active, monthKey) {
  const keys = otherMonthRestDateKeys(
    timesheets,
    employee?.employeeId,
    active?.id,
    monthKey,
  );
  for (const key of countableRestDateKeys(employee)) {
    if (key.slice(0, 7) === monthKey) keys.add(key);
  }
  return keys.size;
}

function restQuotaBlockReason(employee, date, timesheets, active) {
  if (!employee || employee.isCoverage) return "";
  if (active?.scope === "INTERNAL_CONTROL") return "";
  const adding = String(date).slice(0, 10);
  const weekKeys = new Set(countableRestDateKeys(employee));
  if (weekKeys.has(adding)) return "";
  weekKeys.add(adding);
  if (weekKeys.size > MAX_WEEKLY_REST_DAYS) {
    return WEEKLY_REST_QUOTA_NOTE;
  }
  const monthKey = adding.slice(0, 7);
  const monthCap = monthlyRestCapForEmployee(employee, monthKey);
  const monthKeys = otherMonthRestDateKeys(
    timesheets,
    employee.employeeId,
    active?.id,
    monthKey,
  );
  for (const key of weekKeys) {
    if (key.slice(0, 7) === monthKey) monthKeys.add(key);
  }
  if (monthKeys.size > monthCap) {
    return `الراحة الأسبوعية ${monthCap} أيام فقط طول الشهر، ولا يمكن الزيادة عن ذلك.`;
  }
  return "";
}

function findActiveWeekPlan(timesheets, { weekStart, scope, branchId } = {}) {
  return (
    (timesheets || []).find((ts) => {
      if (ts.weekStartDate !== weekStart) return false;
      if (scope && ts.scope !== scope) return false;
      if (branchId && ts.branchId !== branchId) return false;
      return isActiveTimesheetStatus(ts.status);
    }) || null
  );
}

const HQ_COVERAGE_SCOPES = [
  { scope: "MODERATOR", fallbackName: "الموديريتور", scopeLabel: "موديريتور" },
  { scope: "WAREHOUSE", fallbackName: "المخزن", scopeLabel: "مخزن" },
];

function listHqCoverageRows(timesheets, weekStart) {
  return HQ_COVERAGE_SCOPES.flatMap(({ scope, fallbackName, scopeLabel }) => {
    const plans = (timesheets || []).filter(
      (ts) =>
        ts.weekStartDate === weekStart &&
        ts.scope === scope &&
        isActiveTimesheetStatus(ts.status),
    );
    if (plans.length === 0) {
      return [
        {
          id: `hq-${scope}`,
          scope,
          scopeLabel,
          name: fallbackName,
          plan: null,
          needsPlan: true,
        },
      ];
    }
    return plans.map((plan) => ({
      id: plan.id,
      scope,
      scopeLabel,
      name: plan.department?.name || fallbackName,
      plan,
      needsPlan: false,
    }));
  });
}

function hqTimesheetSortRank(scope) {
  if (scope === "MODERATOR") return 0;
  if (scope === "WAREHOUSE") return 1;
  return 2;
}

function listWeekDates(weekOf) {
  const { weekStart, weekEnd } = getWeekRangeContainingIso(weekOf);
  if (!weekStart || !weekEnd) return [];
  const dates = [];
  const start = new Date(`${weekStart}T00:00:00.000Z`);
  const end = new Date(`${weekEnd}T00:00:00.000Z`);
  const cursor = new Date(start);
  while (cursor <= end) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

function employeePlaceLabel(emp) {
  return (
    emp?.branch?.name ||
    emp?.branchName ||
    emp?.department?.name ||
    emp?.departmentName ||
    "الإدارة"
  );
}

function encodeCellValue(entry) {
  if (!entry) return "REST";
  if (entry.dayType === "COVERAGE") return "COVERAGE";
  if (entry.dayType === "REST") return "REST";
  if (entry.shift === "INVENTORY") return "INVENTORY";
  if (entry.workHalfPeriod) {
    return `${entry.shift || "MORNING"}:${entry.workHalfPeriod}`;
  }
  return entry.shift || "MORNING";
}

function parseCellValue(value) {
  if (value === "REST") {
    return { dayType: "REST", shift: null, workHalfPeriod: null };
  }
  if (value === "COVERAGE") {
    return { dayType: "COVERAGE", shift: null, workHalfPeriod: null };
  }
  if (value === "INVENTORY") {
    return { dayType: "WORK", shift: "INVENTORY", workHalfPeriod: null };
  }
  if (typeof value === "string" && value.includes(":")) {
    const [shift, workHalfPeriod] = value.split(":");
    return { dayType: "WORK", shift, workHalfPeriod };
  }
  return { dayType: "WORK", shift: value, workHalfPeriod: null };
}

function defaultSchedule(branchHours) {
  const hours = getShiftHours("MORNING", null, branchHours);
  return {
    shift: "MORNING",
    scheduledStartTime: hours.start,
    scheduledEndTime: hours.end,
    coverageDate: "",
    coverageDates: [],
    workHalfPeriod: "",
  };
}

function shiftLabel(shift) {
  return SHIFT_LABELS[shift] || shift || "—";
}

function formatDisplayDate(isoDate) {
  if (!isoDate || typeof isoDate !== "string") return "";
  const match = isoDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return isoDate;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function coverageChip(plan) {
  if (!plan) {
    return { label: "تحتاج خطة", tone: "missing" };
  }
  return {
    label: STATUS_LABELS[plan.status] || plan.status,
    tone: String(plan.status || "").toLowerCase(),
  };
}

/** Visual cell kind matching the classic weekly branch plan sheet. */
function getEntryCellKind(entry) {
  if (!entry) return "empty";
  if (entry.dayType === "LEAVE") {
    return entry.leaveType === "ANNUAL" ? "annual" : "leave";
  }
  if (entry.dayType === "COVERAGE") return "coverage";
  if (entry.dayType === "REST") return "rest";
  if (entry.shift === "INVENTORY") return "inventory";
  if (entry.workHalfPeriod) {
    return entry.shift === "EVENING" ? "half-evening" : "half-morning";
  }
  if (entry.leaveHalfPeriod) {
    return entry.shift === "EVENING" ? "half-evening" : "half-morning";
  }
  if (entry.shift === "BOTH") return "full";
  if (entry.shift === "NIGHT") return "night";
  if (entry.shift === "EVENING") return "evening";
  return "morning";
}

function isCreatorFinalTimesheetScope(scope) {
  return (
    scope === "INTERNAL_CONTROL" ||
    scope === "BRANCH" ||
    scope === "CASHIER" ||
    scope === "WAREHOUSE" ||
    scope === "MODERATOR"
  );
}

function getEntryCellLabel(entry, scope) {
  if (!entry) return "—";
  if (entry.dayType === "LEAVE") return "—";
  if (entry.dayType === "COVERAGE") return "تغطية (فرع آخر)";
  if (entry.dayType === "REST") return "راحة أسبوعية";
  if (entry.shift === "INVENTORY") return "جرد";
  if (
    (scope === "INTERNAL_CONTROL" ||
      scope === "WAREHOUSE" ||
      scope === "MODERATOR" ||
      scope === "BRANCH") &&
    entry.scheduledStartTime &&
    entry.scheduledEndTime
  ) {
    const range = `${toDisplayHour(entry.scheduledStartTime)}–${toDisplayHour(entry.scheduledEndTime)}`;
    if (scope === "WAREHOUSE" && entry.dayType === "WORK") {
      const withBreak = Number(entry.expectedBreakHours) > 0;
      return withBreak ? `${range} · بريك` : `${range} · بدون بريك`;
    }
    return range;
  }
  const halfLabel =
    entry.workHalfPeriod === "FIRST_HALF"
      ? "نص أول"
      : entry.workHalfPeriod === "SECOND_HALF"
        ? "نص تاني"
        : entry.leaveHalfPeriod
          ? "نص يوم"
          : "";
  if (halfLabel) {
    const shiftPart =
      entry.shift === "EVENING"
        ? "مسائي"
        : entry.shift === "BOTH"
          ? "كامل"
          : "صباحي";
    return `${halfLabel} ${shiftPart}`;
  }
  return shiftLabel(entry.shift);
}

function buildDayTotals(employees, weekDates) {
  const totals = weekDates.map(() => ({
    night: 0,
    morning: 0,
    evening: 0,
    rest: 0,
    coverage: 0,
    annual: 0,
    strength: 0,
  }));

  for (const emp of employees) {
    const byDate = new Map((emp.entries || []).map((e) => [e.date, e]));
    weekDates.forEach((date, idx) => {
      const entry = byDate.get(date);
      if (!entry) return;
      const kind = getEntryCellKind(entry);
      if (kind === "night") {
        totals[idx].night += 1;
        totals[idx].strength += 1;
      } else if (kind === "morning" || kind === "half-morning") {
        totals[idx].morning += 1;
        totals[idx].strength += 1;
      } else if (
        kind === "evening" ||
        kind === "half-evening" ||
        kind === "full" ||
        kind === "inventory"
      ) {
        totals[idx].evening += 1;
        totals[idx].strength += 1;
      } else if (kind === "coverage") {
        totals[idx].coverage += 1;
      } else if (kind === "rest") {
        totals[idx].rest += 1;
      } else if (kind === "annual") {
        totals[idx].annual += 1;
      } else if (kind === "leave") {
        totals[idx].annual += 1;
      }
    });
  }

  return totals;
}

export default function BranchTimesheet() {
  const { user } = useAuth();
  const userBranchId = user?.branchId || "";
  const isCashierManager = user?.jobTitle === "مدير الكاشيرية";
  const isBranchManager = Boolean(
    user?.backendRoles?.includes("branch_manager"),
  );
  const isMarketingManager = isMarketingTimesheetCreator(user);
  const isModeratorLeader = isModeratorTimesheetCreator(user);
  const isWarehouseManager = isWarehouseTimesheetCreator(user);
  const isInternalControlManager = isInternalControlTimesheetCreator(user);
  const isInternalControlMember = isInternalControlDepartmentMember(user);
  const isHrOverview = Boolean(
    isHrFamilyRole(user?.role) ||
      user?.backendRoles?.some((r) =>
        ["hr", "hr_staff", "gm", "admin"].includes(r),
      ),
  );
  /** HR / GM / Admin: create/edit/approve weekly plans (free times), not a scoped manager. */
  const isHrPlanCreator = Boolean(
    isHrOverview &&
      !isBranchManager &&
      !isCashierManager &&
      !isMarketingManager &&
      !isModeratorLeader &&
      !isWarehouseManager &&
      !isInternalControlManager,
  );
  const showBranchCoverageBoard =
    isHrOverview || isCashierManager || isInternalControlMember;
  const showHqCoverageBoard = Boolean(
    isHrOverview && !isInternalControlMember && !isCashierManager,
  );
  const canCreate =
    isBranchManager ||
    isCashierManager ||
    isMarketingManager ||
    isModeratorLeader ||
    isWarehouseManager ||
    isInternalControlManager ||
    isHrPlanCreator;
  const canAccessTimesheet = Boolean(
    canCreate ||
      isInternalControlMember ||
      user?.backendRoles?.some((r) =>
        [
          "manager",
          "branch_manager",
          "regional_manager",
          "department_manager",
          "hr",
          "hr_staff",
          "gm",
          "admin",
        ].includes(r),
      ),
  );

  const [weekOf, setWeekOf] = useState(toInputDate());
  const [selectedBranchId, setSelectedBranchId] = useState(userBranchId);
  const [allBranches, setAllBranches] = useState([]);
  const [branchShiftHours, setBranchShiftHours] = useState(SYSTEM_SHIFT_HOURS);
  const [customShiftPresets, setCustomShiftPresets] = useState([]);
  const [cashierConfigHours, setCashierConfigHours] = useState(
    SYSTEM_CASHIER_SHIFT_HOURS,
  );
  const [warehouseDefaults, setWarehouseDefaults] = useState({
    start: "09:00",
    end: "19:00",
  });
  const [employees, setEmployees] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [schedulesByEmployee, setSchedulesByEmployee] = useState({});
  const [hrEmployeeSearch, setHrEmployeeSearch] = useState("");
  const [hrPlanAddSearch, setHrPlanAddSearch] = useState("");
  const [icAssignRows, setIcAssignRows] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canManageActiveTimesheet = Boolean(
    canCreate &&
      (isInternalControlManager
        ? active?.scope === "INTERNAL_CONTROL"
        : active?.scope !== "INTERNAL_CONTROL"),
  );
  const canEditEntries =
    canManageActiveTimesheet &&
    (active?.status === "DRAFT" ||
      active?.status === "APPROVED" ||
      (active?.status === "PENDING" &&
        isCreatorFinalTimesheetScope(active?.scope)));
  const isAmendmentDraft = Boolean(
    active?.status === "DRAFT" &&
      active?.requiresResubmit &&
      !isCreatorFinalTimesheetScope(active?.scope),
  );

  const jobTitleByEmployeeId = useMemo(() => {
    const map = {};
    for (const emp of employees) {
      if (emp?.id) map[emp.id] = emp.jobTitle || "";
    }
    return map;
  }, [employees]);

  const activeWeekDates = useMemo(() => {
    if (!active?.employees?.length) return [];
    const first = active.employees.find((e) => e.entries?.length);
    return (first?.entries || []).map((e) => e.date);
  }, [active]);

  const dayTotals = useMemo(() => {
    if (!active?.employees || activeWeekDates.length === 0) return [];
    return buildDayTotals(active.employees, activeWeekDates);
  }, [active, activeWeekDates]);

  const planTitle = useMemo(() => {
    if (!active) return "";
    if (active.scope === "CASHIER") {
      return `الخطة الأسبوعية ل${active.branch?.name || "الفرع"}`;
    }
    if (active.scope === "WAREHOUSE") {
      return `خطة المخزن الأسبوعية — ${formatDisplayDate(active.weekStartDate)} إلى ${formatDisplayDate(active.weekEndDate)}`;
    }
    if (active.scope === "DEPARTMENT") {
      return `الخطة الأسبوعية ل${active.department?.name || "التسويق"}`;
    }
    if (active.scope === "MODERATOR") {
      return `خطة الموديريتور الأسبوعية — ${active.department?.name || "الفريق"}`;
    }
    if (active.scope === "INTERNAL_CONTROL") {
      return `خطة الرقابة الداخلية — كاميرات ${active.branch?.name || "الفرع"}`;
    }
    return `الخطة الأسبوعية ل${active.branch?.name || "الفرع"}`;
  }, [active]);

  const allowBothShift = Boolean(
    isCashierManager || active?.scope === "CASHIER",
  );
  const allowInventoryShift = allowBothShift;

  const canApproveActive = useMemo(() => {
    if (!active || !user) return false;
    const mapped = mapTimesheetToPendingRequest(active);
    if (canUserApproveTimesheetRequest(mapped, user)) return true;

    // Fallback when session managedBranchIds/dept is incomplete: if the sheet
    // is already in this user's loaded list and their role matches the step,
    // show actions (API still enforces branch/dept scope).
    if (mapped.status !== "pending") return false;
    if (!timesheets.some((t) => t.id === active.id)) return false;
    const slugs = user.backendRoles || [];
    if (
      slugs.includes("hr") &&
      !slugs.some((s) =>
        ["regional_manager", "department_manager", "gm", "admin"].includes(s),
      )
    ) {
      return false;
    }
    const currentStep = Number(mapped.currentStep);
    const steps = mapped.workflow?.steps || [];
    const activeStep = steps.find(
      (step) => Number(step.stepOrder) === currentStep,
    );
    const requiredSlug = activeStep?.approverRole?.slug;
    return Boolean(requiredSlug && slugs.includes(requiredSlug));
  }, [active, user, timesheets]);

  const availableBranches = useMemo(() => {
    if (isCashierManager || isInternalControlMember || isHrPlanCreator) {
      return allBranches
        .filter((b) => b.isActive !== false)
        .map((b) => ({
          id: b.id,
          name: b.name || b.id,
          code: b.code || "",
        }));
    }
    const map = new Map();
    for (const emp of employees) {
      const id = emp.branchId || emp.branch?.id;
      if (!id) continue;
      if (!map.has(id)) {
        map.set(id, {
          id,
          name: emp.branch?.name || id,
          code: emp.branch?.code || "",
        });
      }
    }
    return Array.from(map.values());
  }, [
    allBranches,
    employees,
    isCashierManager,
    isInternalControlMember,
    isHrPlanCreator,
  ]);

  /** Cashier manager: all ACC cashiers. Branch manager: own staff + coverage pool. */
  const employeesForBranch = useMemo(() => {
    if (
      isMarketingManager ||
      isModeratorLeader ||
      isWarehouseManager ||
      isInternalControlManager ||
      isHrPlanCreator
    ) {
      return employees;
    }
    let list = employees;
    if (isCashierManager) {
      list = list.filter(
        (emp) =>
          emp.jobTitle === "كاشير" &&
          (emp.department?.code === "ACC" || emp.departmentCode === "ACC"),
      );
      if (!selectedBranchId) return [];
      return list;
    }
    return list;
  }, [
    employees,
    selectedBranchId,
    isMarketingManager,
    isModeratorLeader,
    isWarehouseManager,
    isInternalControlManager,
    isHrPlanCreator,
    isCashierManager,
  ]);

  const hrEmployeesVisible = useMemo(() => {
    if (!isHrPlanCreator) return employeesForBranch;
    const q = hrEmployeeSearch.trim();
    if (!q) {
      // Without a query, only keep already-selected cards visible.
      if (!selectedIds.length) return [];
      const selectedSet = new Set(selectedIds);
      return employeesForBranch.filter((emp) => selectedSet.has(emp.id));
    }
    return employeesForBranch.filter(
      (emp) =>
        selectedIds.includes(emp.id) || employeeMatchesSearch(emp, q),
    );
  }, [
    isHrPlanCreator,
    employeesForBranch,
    hrEmployeeSearch,
    selectedIds,
  ]);

  const hrPlanAddMatches = useMemo(() => {
    if (!isHrPlanCreator) return [];
    const q = hrPlanAddSearch.trim();
    if (!q) return [];
    const onPlan = new Set(
      (active?.employees || []).map((row) => row.employeeId),
    );
    return employeesForBranch
      .filter((emp) => !onPlan.has(emp.id) && employeeMatchesSearch(emp, q))
      .slice(0, 12);
  }, [
    isHrPlanCreator,
    hrPlanAddSearch,
    employeesForBranch,
    active?.employees,
  ]);

  const branchIdForCreate =
    isCashierManager || isInternalControlManager || isHrPlanCreator
      ? selectedBranchId
      : userBranchId || selectedBranchId;

  const selectedWeekRange = useMemo(
    () => getWeekRangeContainingIso(weekOf),
    [weekOf],
  );

  const timesheetsForWeek = useMemo(() => {
    const { weekStart } = selectedWeekRange;
    const list = !weekStart
      ? timesheets
      : timesheets.filter((ts) => ts.weekStartDate === weekStart);
    if (!showHqCoverageBoard) return list;
    return [...list].sort(
      (a, b) => hqTimesheetSortRank(a.scope) - hqTimesheetSortRank(b.scope),
    );
  }, [timesheets, selectedWeekRange, showHqCoverageBoard]);

  const branchCoverage = useMemo(() => {
    if (!showBranchCoverageBoard) {
      return { missing: [], ready: [] };
    }
    const { weekStart } = selectedWeekRange;
    if (!weekStart) return { missing: [], ready: [] };
    const branches = (allBranches.length
      ? allBranches
      : availableBranches
    ).filter((b) => b.isActive !== false);
    const rows = branches.map((branch) => {
      const ic = isInternalControlMember
        ? findActiveWeekPlan(timesheets, {
            weekStart,
            scope: "INTERNAL_CONTROL",
            branchId: branch.id,
          })
        : null;
      const ops = isCashierManager
        ? null
        : findActiveWeekPlan(timesheets, {
            weekStart,
            scope: "BRANCH",
            branchId: branch.id,
          });
      const cashier = findActiveWeekPlan(timesheets, {
        weekStart,
        scope: "CASHIER",
        branchId: branch.id,
      });
      const needsPlan = isInternalControlManager
        ? !ic
        : isInternalControlMember
          ? !ops && !cashier && !ic
          : isCashierManager
            ? !cashier
            : !ops || !cashier;
      return { branch, ops, cashier, ic, needsPlan };
    });
    const byName = (a, b) =>
      String(a.branch.name || "").localeCompare(
        String(b.branch.name || ""),
        "ar",
      );
    return {
      missing: rows.filter((row) => row.needsPlan).sort(byName),
      ready: rows.filter((row) => !row.needsPlan).sort(byName),
    };
  }, [
    showBranchCoverageBoard,
    selectedWeekRange,
    allBranches,
    availableBranches,
    isCashierManager,
    isInternalControlMember,
    isInternalControlManager,
    timesheets,
  ]);

  const hqCoverage = useMemo(() => {
    if (!showHqCoverageBoard) {
      return { missing: [], ready: [] };
    }
    const { weekStart } = selectedWeekRange;
    if (!weekStart) return { missing: [], ready: [] };
    const rows = listHqCoverageRows(timesheets, weekStart);
    return {
      missing: rows.filter((row) => row.needsPlan),
      ready: rows.filter((row) => !row.needsPlan),
    };
  }, [showHqCoverageBoard, selectedWeekRange, timesheets]);

  const missingPlanWarning = useMemo(() => {
    if (!canCreate) return null;
    const { weekStart, weekEnd } = selectedWeekRange;
    if (isWarehouseManager) {
      const found = timesheets.find(
        (ts) =>
          ts.scope === "WAREHOUSE" &&
          ts.weekStartDate === weekStart &&
          isActiveTimesheetStatus(ts.status),
      );
      if (found) return null;
      return {
        title: "هذا الأسبوع يحتاج خطة مخزن أسبوعية",
        dates: weekStart
          ? `${formatDisplayDate(weekStart)} → ${formatDisplayDate(weekEnd)}`
          : undefined,
      };
    }
    if (!weekStart) return null;
    if (isModeratorLeader) {
      const found = findActiveWeekPlan(timesheets, {
        weekStart,
        scope: "MODERATOR",
      });
      if (found) return null;
      return {
        title: "هذا الأسبوع يحتاج خطة أسبوعية للموديريتور",
        dates: `${formatDisplayDate(weekStart)} → ${formatDisplayDate(weekEnd)}`,
      };
    }
    if (isMarketingManager) {
      const found = findActiveWeekPlan(timesheets, {
        weekStart,
        scope: "DEPARTMENT",
      });
      if (found) return null;
      return {
        title: "هذا الأسبوع يحتاج خطة أسبوعية للتسويق",
        dates: `${formatDisplayDate(weekStart)} → ${formatDisplayDate(weekEnd)}`,
      };
    }
    if (isInternalControlManager) {
      const missingRows = branchCoverage.missing;
      if (missingRows.length === 0) return null;
      const selectedMissing = missingRows.find(
        (row) => row.branch.id === selectedBranchId,
      );
      const focusName =
        selectedMissing?.branch?.name ||
        (missingRows.length === 1 ? missingRows[0].branch.name : "");
      return {
        title: focusName
          ? `هذا الأسبوع يحتاج خطة رقابة داخلية — ${focusName}`
          : `هذا الأسبوع يحتاج خطة رقابة داخلية — ${missingRows.length} فروع بدون خطة`,
        dates: `${formatDisplayDate(weekStart)} → ${formatDisplayDate(weekEnd)}`,
      };
    }
    if (isCashierManager) {
      const missingRows = branchCoverage.missing;
      if (missingRows.length === 0) return null;
      const selectedMissing = missingRows.find(
        (row) => row.branch.id === selectedBranchId,
      );
      const focusName =
        selectedMissing?.branch?.name ||
        (missingRows.length === 1 ? missingRows[0].branch.name : "");
      return {
        title: focusName
          ? `هذا الأسبوع يحتاج خطة أسبوعية للكاشيرية — ${focusName}`
          : `هذا الأسبوع يحتاج خطة أسبوعية للكاشيرية — ${missingRows.length} فروع بدون خطة`,
        dates: `${formatDisplayDate(weekStart)} → ${formatDisplayDate(weekEnd)}`,
      };
    }
    if (isBranchManager) {
      const found = findActiveWeekPlan(timesheets, {
        weekStart,
        scope: "BRANCH",
        branchId: userBranchId,
      });
      if (found) return null;
      return {
        title: "هذا الأسبوع يحتاج خطة أسبوعية",
        dates: `${formatDisplayDate(weekStart)} → ${formatDisplayDate(weekEnd)}`,
      };
    }
    return null;
  }, [
    canCreate,
    selectedWeekRange,
    isWarehouseManager,
    isModeratorLeader,
    isMarketingManager,
    isInternalControlManager,
    isCashierManager,
    isBranchManager,
    timesheets,
    weekOf,
    selectedBranchId,
    userBranchId,
    branchCoverage,
  ]);

  const loadBranches = useCallback(async () => {
    if (
      !canAccessTimesheet ||
      (!isCashierManager &&
        !isBranchManager &&
        !isHrOverview &&
        !isInternalControlMember)
    ) {
      return;
    }
    try {
      const rows = await getBranches();
      const list = Array.isArray(rows)
        ? rows
        : Array.isArray(rows?.data)
          ? rows.data
          : [];
      setAllBranches(list);
      if (
        list.length > 0 &&
        (isCashierManager || isBranchManager || isInternalControlManager)
      ) {
        setSelectedBranchId((prev) => prev || list[0].id || "");
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل الفروع"));
    }
  }, [
    canAccessTimesheet,
    isCashierManager,
    isBranchManager,
    isHrOverview,
    isHrPlanCreator,
    isInternalControlMember,
    isInternalControlManager,
  ]);

  const loadEmployees = useCallback(async () => {
    if (!canAccessTimesheet || !canCreate) return;
    try {
      const params =
        isMarketingManager ||
        isModeratorLeader ||
        isWarehouseManager ||
        isCashierManager ||
        isInternalControlManager ||
        isHrPlanCreator
          ? undefined
          : userBranchId || undefined;
      const rows = await getBranchTimesheetEmployees(params);
      const list = Array.isArray(rows) ? rows : [];
      const scoped = isCashierManager
        ? list.filter(
            (emp) =>
              emp.jobTitle === "كاشير" &&
              (emp.department?.code === "ACC" ||
                emp.departmentCode === "ACC"),
          )
        : list;
      setEmployees(scoped);
      if (
        !isCashierManager &&
        !isWarehouseManager &&
        !isInternalControlManager &&
        !isHrPlanCreator &&
        userBranchId
      ) {
        setSelectedBranchId(userBranchId);
      }
    } catch (err) {
      setError(
        getApiErrorMessage(
          err,
          isWarehouseManager
            ? "تعذر تحميل موظفي المخازن"
            : isModeratorLeader
              ? "تعذر تحميل فريق الموديريتور"
              : isMarketingManager
              ? "تعذر تحميل موظفي التسويق"
              : isInternalControlManager
                ? "تعذر تحميل موظفي الرقابة الداخلية"
                : isCashierManager
                  ? "تعذر تحميل كاشيرية الفروع"
                  : isHrPlanCreator
                    ? "تعذر تحميل الموظفين"
                    : "تعذر تحميل موظفي الفرع",
        ),
      );
    }
  }, [
    canAccessTimesheet,
    canCreate,
    isCashierManager,
    isMarketingManager,
    isModeratorLeader,
    isWarehouseManager,
    isInternalControlManager,
    isHrPlanCreator,
    userBranchId,
  ]);

  const loadTimesheets = useCallback(async () => {
    if (!canAccessTimesheet) return;
    try {
      const params =
        isBranchManager && userBranchId
          ? { branchId: userBranchId }
          : undefined;
      const rows = await listTimesheets(params);
      setTimesheets(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل الخطط الأسبوعية"));
    }
  }, [canAccessTimesheet, isBranchManager, userBranchId]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadTimesheets();
  }, [loadTimesheets]);

  useEffect(() => {
    if (!isHrPlanCreator) return;
    // Work branch is optional for HR — start with none so plans use employee home branch.
    setSelectedBranchId("");
  }, [isHrPlanCreator]);

  useEffect(() => {
    let cancelled = false;
    getShiftHoursConfig()
      .then((config) => {
        if (cancelled || !config) return;
        setCustomShiftPresets(
          Array.isArray(config.customShifts) ? config.customShifts : [],
        );
        if (config.cashier) {
          setCashierConfigHours(resolveShiftHoursConfig(config.cashier));
        }
        if (config.warehouse?.start && config.warehouse?.end) {
          setWarehouseDefaults({
            start: config.warehouse.start,
            end: config.warehouse.end,
          });
        }
      })
      .catch(() => {
        /* keep local defaults */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setActive((prev) => {
      if (!prev) return prev;
      if (isWarehouseManager) {
        return prev.weekStartDate === weekOf ? prev : null;
      }
      const { weekStart } = getWeekRangeContainingIso(weekOf);
      return prev.weekStartDate === weekStart ? prev : null;
    });
  }, [weekOf, isWarehouseManager]);

  useEffect(() => {
    if (isCashierManager) {
      setBranchShiftHours(cashierConfigHours);
      return;
    }
    if (
      isMarketingManager ||
      isModeratorLeader ||
      isInternalControlManager ||
      active?.scope === "INTERNAL_CONTROL" ||
      active?.scope === "DEPARTMENT" ||
      active?.scope === "MODERATOR" ||
      active?.scope === "WAREHOUSE"
    ) {
      setBranchShiftHours(SYSTEM_SHIFT_HOURS);
      return;
    }
    const branchId =
      active?.branchId ||
      (!isMarketingManager && !isModeratorLeader ? branchIdForCreate : null) ||
      null;
    if (!branchId) {
      setBranchShiftHours(SYSTEM_SHIFT_HOURS);
      return;
    }
    let cancelled = false;
    getBranch(branchId)
      .then((branch) => {
        if (!cancelled) {
          setBranchShiftHours(
            resolveShiftHoursConfig(branch?.timesheetShiftHours),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setBranchShiftHours(SYSTEM_SHIFT_HOURS);
      });
    return () => {
      cancelled = true;
    };
  }, [
    active?.branchId,
    active?.scope,
    branchIdForCreate,
    cashierConfigHours,
    isCashierManager,
    isMarketingManager,
    isModeratorLeader,
    isInternalControlManager,
  ]);

  const ensureSchedule = (id) => {
    setSchedulesByEmployee((prev) =>
      prev[id]
        ? prev
        : {
            ...prev,
            [id]: isInternalControlManager
              ? defaultInternalControlSchedule()
              : isModeratorLeader
                ? defaultModeratorSchedule()
                : defaultSchedule(branchShiftHours),
          },
    );
  };

  const toggleEmployee = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        setSchedulesByEmployee((schedules) => {
          const next = { ...schedules };
          delete next[id];
          return next;
        });
        return prev.filter((x) => x !== id);
      }
      ensureSchedule(id);
      return [...prev, id];
    });
  };

  const updateSchedule = (employeeId, patch) => {
    setSchedulesByEmployee((prev) => {
      const current = prev[employeeId] || defaultSchedule(branchShiftHours);
      let next = { ...current, ...patch };
      if (patch.shift && !patch.scheduledStartTime && !patch.scheduledEndTime) {
        const defaults = isModeratorLeader
          ? getModeratorShiftHours(patch.shift)
          : getShiftHours(patch.shift, null, branchShiftHours);
        next = {
          ...next,
          scheduledStartTime: defaults.start,
          scheduledEndTime: defaults.end,
        };
        if (patch.shift === "INVENTORY") {
          next.workHalfPeriod = "";
        }
      }
      return { ...prev, [employeeId]: next };
    });
  };

  const toggleCoverageDate = (employeeId, date) => {
    setSchedulesByEmployee((prev) => {
      const current = prev[employeeId] || defaultSchedule(branchShiftHours);
      const selected = new Set(current.coverageDates || []);
      if (selected.has(date)) selected.delete(date);
      else selected.add(date);
      return {
        ...prev,
        [employeeId]: { ...current, coverageDates: [...selected].sort() },
      };
    });
  };

  async function handleAssignIcWatches() {
    if (!isInternalControlManager) return;
    if (icAssignRows.length === 0) {
      setError("أضف تعيين مراقبة واحد على الأقل");
      return;
    }
    const weekBounds = getWeekRangeContainingIso(weekOf);
    for (const row of icAssignRows) {
      if (!row.employeeId || !row.branchId || !row.date) {
        setError("أكمل الموظف والفرع واليوم لكل صف");
        return;
      }
      if (row.date < weekBounds.weekStart || row.date > weekBounds.weekEnd) {
        setError("كل الأيام لازم تكون داخل الأسبوع المحدد (سبت–جمعة)");
        return;
      }
      if (!row.scheduledStartTime || !row.scheduledEndTime) {
        setError("حدد من وإلى لكل تعيين");
        return;
      }
    }
    const dayKeys = new Set();
    for (const row of icAssignRows) {
      const key = `${row.employeeId}:${row.date}`;
      if (dayKeys.has(key)) {
        setError("نفس الموظف متعيّن أكثر من مرة في نفس اليوم");
        return;
      }
      dayKeys.add(key);
    }

    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const sheets = await assignInternalControlWatches({
        assignments: icAssignRows.map((row) => ({
          employeeId: row.employeeId,
          branchId: row.branchId,
          date: row.date,
          scheduledStartTime: normalizeHhMm(row.scheduledStartTime),
          scheduledEndTime: normalizeHhMm(row.scheduledEndTime),
        })),
      });
      setIcAssignRows([]);
      setSuccess(
        `تم اعتماد التعيينات — تظهر لكل أعضاء الرقابة`,
      );
      await loadTimesheets();
      const list = Array.isArray(sheets) ? sheets : [];
      if (list[0]) setActive(list[0]);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر اعتماد تعيينات الرقابة"));
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (isWarehouseManager) {
      if (!user?.departmentBackendId) {
        setError("حسابك غير مرتبط بإدارة المخازن");
        return;
      }
      if (selectedIds.length === 0) {
        setError("اختر موظفين على الأقل");
        return;
      }
      const employeeSchedules = selectedIds.map((employeeId) => {
        const schedule =
          schedulesByEmployee[employeeId] || defaultWarehouseSchedule();
        return {
          employeeId,
          shift: "MORNING",
          scheduledStartTime: normalizeHhMm(
            schedule.scheduledStartTime || "09:00",
          ),
          scheduledEndTime: normalizeHhMm(
            schedule.scheduledEndTime || "19:00",
          ),
          hasBreak: Boolean(schedule.hasBreak),
        };
      });
      const missing = employeeSchedules.find(
        (row) => !row.scheduledStartTime || !row.scheduledEndTime,
      );
      if (missing) {
        setError("حدد مواعيد الحضور والانصراف لكل موظف");
        return;
      }

      setLoading(true);
      setError("");
      setSuccess("");
      try {
        const created = await createTimesheet({
          departmentId: user.departmentBackendId,
          weekOf,
          employeeIds: selectedIds,
          employeeSchedules,
        });
        setSuccess(
          "تم إنشاء مسودة خطة المخزن الأسبوعية — راجع الجدول ثم اضغط اعتماد نهائي",
        );
        setActive(created);
        await loadTimesheets();
      } catch (err) {
        setError(getApiErrorMessage(err, "تعذر إنشاء خطة المخزن الأسبوعية"));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (isHrPlanCreator) {
      if (selectedIds.length === 0) {
        setError("اختر موظفين على الأقل");
        return;
      }

      const buildSchedulesFor = (employeeIds, workBranchId) =>
        employeeIds.map((employeeId) => {
          const schedule =
            schedulesByEmployee[employeeId] || defaultWarehouseSchedule();
          const emp = employees.find((e) => e.id === employeeId);
          const homeId = emp?.branchId || emp?.branch?.id || "";
          const isCoverage = Boolean(
            workBranchId && homeId && homeId !== workBranchId,
          );
          const coverageDates = Array.isArray(schedule.coverageDates)
            ? schedule.coverageDates.filter(Boolean)
            : schedule.coverageDate
              ? [schedule.coverageDate]
              : [];
          const row = {
            employeeId,
            shift: "MORNING",
            scheduledStartTime: normalizeHhMm(
              schedule.scheduledStartTime || "09:00",
            ),
            scheduledEndTime: normalizeHhMm(
              schedule.scheduledEndTime || "19:00",
            ),
            hasBreak: Boolean(schedule.hasBreak),
          };
          if (isCoverage && coverageDates.length > 0) {
            row.coverageDates = coverageDates;
          }
          return row;
        });

      const weekBounds = getWeekRangeContainingIso(weekOf);

      const validateSchedules = (employeeSchedules) => {
        const missing = employeeSchedules.find(
          (row) => !row.scheduledStartTime || !row.scheduledEndTime,
        );
        if (missing) {
          return "حدد مواعيد الحضور والانصراف لكل موظف";
        }
        const outOfWeek = employeeSchedules.find((row) => {
          if (!row.coverageDates?.length) return false;
          return row.coverageDates.some(
            (d) => d < weekBounds.weekStart || d > weekBounds.weekEnd,
          );
        });
        if (outOfWeek) {
          return "أيام التغطية يجب أن تكون داخل الأسبوع المحدد (سبت–جمعة)";
        }
        return "";
      };

      /** Optional work branch → one plan. Else group by each employee's home branch. */
      const groups = [];
      if (selectedBranchId) {
        groups.push({
          branchId: selectedBranchId,
          employeeIds: selectedIds,
        });
      } else {
        const byHome = new Map();
        for (const employeeId of selectedIds) {
          const emp = employees.find((e) => e.id === employeeId);
          const homeId = emp?.branchId || emp?.branch?.id || "";
          if (!homeId) {
            setError(
              `${emp?.fullName || emp?.name || "موظف"} من غير فرع ميداني — اختار فرع العمل عشان يتحط على الخطة`,
            );
            return;
          }
          if (!byHome.has(homeId)) byHome.set(homeId, []);
          byHome.get(homeId).push(employeeId);
        }
        for (const [branchId, employeeIds] of byHome) {
          groups.push({ branchId, employeeIds });
        }
      }

      for (const group of groups) {
        const errMsg = validateSchedules(
          buildSchedulesFor(group.employeeIds, group.branchId),
        );
        if (errMsg) {
          setError(errMsg);
          return;
        }
      }

      setLoading(true);
      setError("");
      setSuccess("");
      try {
        let lastCreated = null;
        let openedExisting = 0;
        let addedCount = 0;
        for (const group of groups) {
          const employeeSchedules = buildSchedulesFor(
            group.employeeIds,
            group.branchId,
          );
          const created = await createTimesheet({
            branchId: group.branchId,
            weekOf,
            employeeIds: group.employeeIds,
            employeeSchedules,
          });
          lastCreated = created;
          if (created?.employeesAdded) addedCount += created.employeesAdded;
          else if (created?.alreadyExists) openedExisting += 1;
        }
        if (addedCount) {
          setSuccess(
            `تم إضافة ${addedCount} موظف للخطة الموجودة — حتى لو مش من نفس الفرع. راجع الجدول ثم اضغط اعتماد نهائي`,
          );
        } else if (groups.length > 1) {
          setSuccess(
            `تم تجهيز ${groups.length} خطط حسب فروع الموظفين` +
              (openedExisting
                ? ` (منها ${openedExisting} موجودة للتعديل)`
                : "") +
              " — راجع ثم اضغط اعتماد نهائي",
          );
        } else if (openedExisting) {
          setSuccess(
            "الموظفون موجودون في الخطة الحالية — عدّلها ثم اضغط اعتماد نهائي",
          );
        } else {
          setSuccess(
            "تم إنشاء المسودة بمواعيد حرة — راجع الجدول ثم اضغط اعتماد نهائي",
          );
        }
        setActive(lastCreated);
        setSelectedIds([]);
        setSchedulesByEmployee({});
        setHrEmployeeSearch("");
        await loadTimesheets();
      } catch (err) {
        setError(getApiErrorMessage(err, "تعذر إنشاء الخطة الأسبوعية"));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!isMarketingManager && !isModeratorLeader && !isInternalControlManager && !branchIdForCreate) {
      setError(
        isCashierManager
          ? "اختر الفرع أولاً"
          : "حسابك غير مرتبط بفرع",
      );
      return;
    }
    if (isInternalControlManager && !branchIdForCreate) {
      setError("اختر الفرع المراد مراقبة كاميراته");
      return;
    }
    if (isInternalControlManager && !user?.departmentBackendId) {
      setError("حسابك غير مرتبط بإدارة الرقابة الداخلية");
      return;
    }
    if (isMarketingManager && !user?.departmentBackendId) {
      setError("حسابك غير مرتبط بقسم التسويق");
      return;
    }
    if (isModeratorLeader && !user?.departmentBackendId) {
      setError("حسابك غير مرتبط بقسم فريق الموديريتور");
      return;
    }
    if (selectedIds.length === 0) {
      setError("اختر موظفين على الأقل");
      return;
    }
    const weekBounds = getWeekRangeContainingIso(weekOf);
    const workBranchId = branchIdForCreate;
    const employeeSchedules = selectedIds.map((employeeId) => {
      const schedule =
        schedulesByEmployee[employeeId] ||
        (isInternalControlManager
          ? defaultInternalControlSchedule()
          : isModeratorLeader
            ? defaultModeratorSchedule()
            : defaultSchedule(branchShiftHours));
      const emp = employeesForBranch.find((e) => e.id === employeeId);
      const homeId = emp?.branchId || emp?.branch?.id || "";
      const isCoverage = Boolean(
        !isInternalControlManager &&
          workBranchId &&
          homeId &&
          homeId !== workBranchId,
      );
      const coverageDates = Array.isArray(schedule.coverageDates)
        ? schedule.coverageDates.filter(Boolean)
        : schedule.coverageDate
          ? [schedule.coverageDate]
          : [];
      const row = {
        employeeId,
        shift: schedule.shift,
        scheduledStartTime: normalizeHhMm(schedule.scheduledStartTime),
        scheduledEndTime: normalizeHhMm(schedule.scheduledEndTime),
      };
      if (isModeratorLeader) {
        const key = MODERATOR_SHIFT_WINDOWS[schedule.shift]
          ? schedule.shift
          : "MORNING";
        const window = MODERATOR_SHIFT_WINDOWS[key];
        row.shift = key;
        row.scheduledStartTime = window.start;
        row.scheduledEndTime = window.end;
        row.hasBreak = false;
      } else if (schedule.workHalfPeriod) {
        row.workHalfPeriod = schedule.workHalfPeriod;
      }
      if (isCoverage) {
        row.coverageDates = coverageDates;
      }
      return row;
    });
    const missing = employeeSchedules.find(
      (row) =>
        !row.shift || !row.scheduledStartTime || !row.scheduledEndTime,
    );
    if (missing) {
      setError("حدد الفترة الافتراضية لكل موظف");
      return;
    }
    const missingCoverage = employeeSchedules.find(
      (row) => row.coverageDates !== undefined && row.coverageDates.length === 0,
    );
    if (missingCoverage) {
      setError("حدد أيام التغطية لكل موظف من فرع آخر (يوم أو أكثر)");
      return;
    }
    const outOfWeek = employeeSchedules.find((row) => {
      if (!row.coverageDates?.length) return false;
      return row.coverageDates.some(
        (d) => d < weekBounds.weekStart || d > weekBounds.weekEnd,
      );
    });
    if (outOfWeek) {
      setError("أيام التغطية يجب أن تكون داخل الأسبوع المحدد (سبت–جمعة)");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const payload = isMarketingManager || isModeratorLeader
        ? {
            departmentId: user.departmentBackendId,
            weekOf,
            employeeIds: selectedIds,
            employeeSchedules,
          }
        : isInternalControlManager
          ? {
              branchId: branchIdForCreate,
              departmentId: user.departmentBackendId,
              weekOf,
              employeeIds: selectedIds,
              employeeSchedules,
            }
          : {
              branchId: branchIdForCreate,
              weekOf,
              employeeIds: selectedIds,
              employeeSchedules,
            };
      const created = await createTimesheet(payload);
      setSuccess(
        isInternalControlManager
          ? "تم اعتماد خطة الرقابة الداخلية تلقائياً — تظهر لكل أعضاء القسم"
          : isModeratorLeader
            ? "تم إنشاء مسودة خطة الموديريتور — راجع التغطية 1 / 1 / 2 ثم اضغط اعتماد نهائي"
            : isCashierManager || isBranchManager
            ? "تم إنشاء المسودة — راجع الجدول ثم اضغط اعتماد نهائي. التعديل بعد الاعتماد مباشر ومن غير موافقة بعدك."
            : "تم إنشاء مسودة الخطة — التغطية ليوم أو أكثر، ونص الشيفت ممكن على فرعين",
      );
      setActive(created);
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إنشاء الخطة الأسبوعية"));
    } finally {
      setLoading(false);
    }
  }

  async function handleAddEmployeeToActivePlan(emp) {
    if (!isHrPlanCreator || !canEditEntries) return;
    if (!active?.id || active.scope !== "BRANCH" || !active.branchId) {
      setError("افتح خطة فرع أولاً عشان تضيف الموظف عليها");
      return;
    }
    if (!emp?.id) return;
    if ((active.employees || []).some((row) => row.employeeId === emp.id)) {
      setError("الموظف موجود في الخطة دي بالفعل");
      return;
    }
    const schedule = schedulesByEmployee[emp.id] || defaultWarehouseSchedule();
    const homeId = emp.branchId || emp.branch?.id || "";
    const isCoverage = Boolean(
      active.branchId && homeId && homeId !== active.branchId,
    );
    const coverageDates = Array.isArray(schedule.coverageDates)
      ? schedule.coverageDates.filter(Boolean)
      : [];
    const row = {
      employeeId: emp.id,
      shift: "MORNING",
      scheduledStartTime: normalizeHhMm(
        schedule.scheduledStartTime || "09:00",
      ),
      scheduledEndTime: normalizeHhMm(schedule.scheduledEndTime || "19:00"),
      hasBreak: Boolean(schedule.hasBreak),
    };
    if (isCoverage && coverageDates.length > 0) {
      row.coverageDates = coverageDates;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const updated = await createTimesheet({
        branchId: active.branchId,
        weekOf: active.weekStartDate || weekOf,
        employeeIds: [emp.id],
        employeeSchedules: [row],
      });
      setActive(updated);
      setHrPlanAddSearch("");
      if (updated?.employeesAdded) {
        setSuccess(
          `تم إضافة ${emp.fullName || emp.name} للخطة — حتى لو مش من الفرع`,
        );
      } else if (updated?.alreadyExists) {
        setSuccess("الموظف موجود في الخطة دي بالفعل");
      } else {
        setSuccess(`تم إضافة ${emp.fullName || emp.name} للخطة`);
      }
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إضافة الموظف للخطة"));
    } finally {
      setLoading(false);
    }
  }

  async function updateWarehouseDay(employeeId, date, patch) {
    if (!active || !canEditEntries || active.scope !== "WAREHOUSE") return;
    if (patch.dayType === "REST") {
      const emp = (active.employees || []).find(
        (row) => row.employeeId === employeeId,
      );
      const blocked = restQuotaBlockReason(emp, date, timesheets, active);
      if (blocked) {
        setError(blocked);
        return;
      }
    }
    setLoading(true);
    setError("");
    try {
      const entry = {
        employeeId,
        date,
        dayType: patch.dayType,
      };
      if (patch.dayType === "WORK") {
        entry.scheduledStartTime = normalizeHhMm(patch.scheduledStartTime);
        entry.scheduledEndTime = normalizeHhMm(patch.scheduledEndTime);
        entry.shift = "MORNING";
        const breakFields = warehouseBreakFields(Boolean(patch.hasBreak));
        entry.expectedPunchCount = breakFields.expectedPunchCount;
        entry.expectedBreakHours = breakFields.expectedBreakHours;
      }
      const updated = await updateTimesheet(active.id, { entries: [entry] });
      setActive(updated);
      setSuccess("تم تحديث يوم الخطة");
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحديث خطة المخزن"));
    } finally {
      setLoading(false);
    }
  }

  async function updateInternalControlDay(employeeId, date, patch) {
    if (!active || !canEditEntries || active.scope !== "INTERNAL_CONTROL") {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const entry = {
        employeeId,
        date,
        dayType: patch.dayType,
      };
      if (patch.dayType === "WORK") {
        entry.scheduledStartTime = normalizeHhMm(patch.scheduledStartTime);
        entry.scheduledEndTime = normalizeHhMm(patch.scheduledEndTime);
        entry.shift = "MORNING";
        entry.expectedPunchCount = 2;
      }
      const updated = await updateTimesheet(active.id, { entries: [entry] });
      setActive(updated);
      setSuccess("تم تحديث يوم مراقبة الكاميرات");
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحديث خطة الرقابة الداخلية"));
    } finally {
      setLoading(false);
    }
  }

  async function updateDayShift(employeeId, date, shift, workHalfPeriod = null) {
    if (!active || !canEditEntries) return;
    if (active.scope === "INTERNAL_CONTROL") {
      const emp = (active.employees || []).find(
        (e) => e.employeeId === employeeId,
      );
      const existing = (emp?.entries || []).find((e) => e.date === date);
      await updateInternalControlDay(employeeId, date, {
        dayType: "WORK",
        scheduledStartTime:
          existing?.scheduledStartTime ||
          emp?.scheduledStartTime ||
          "09:00",
        scheduledEndTime:
          existing?.scheduledEndTime || emp?.scheduledEndTime || "17:00",
      });
      return;
    }
    const hours =
      active.scope === "MODERATOR"
        ? getModeratorShiftHours(shift)
        : getShiftHours(shift, date, branchShiftHours);

    setLoading(true);
    setError("");
    try {
      const updated = await updateTimesheet(active.id, {
        entries: [
          {
            employeeId,
            date,
            dayType: "WORK",
            shift,
            scheduledStartTime: hours.start,
            scheduledEndTime: hours.end,
            workHalfPeriod: workHalfPeriod || null,
          },
        ],
      });
      setActive(updated);
      setSuccess(
          updated.status === "DRAFT" && updated.requiresResubmit
            ? "تم حفظ التعديل — أرسل لإعادة الاعتماد"
            : "تم تحديث وردية اليوم",
      );
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحديث الوردية"));
    } finally {
      setLoading(false);
    }
  }

  async function applyEmployeeSchedule(employee) {
    if (!active || !canEditEntries) return;
    const morningDefaults =
      active.scope === "MODERATOR"
        ? getModeratorShiftHours("MORNING")
        : getShiftHours("MORNING", null, branchShiftHours);
    const schedule =
      schedulesByEmployee[employee.employeeId] ||
      (employee.shift
        ? {
            shift: employee.shift,
            scheduledStartTime:
              employee.scheduledStartTime || morningDefaults.start,
            scheduledEndTime:
              employee.scheduledEndTime || morningDefaults.end,
          }
        : active.scope === "MODERATOR"
          ? defaultModeratorSchedule()
          : defaultSchedule(branchShiftHours));

    const todayKey = cairoCalendarDateKey();
    const workDays = (employee.entries || []).filter(
      (entry) => entry.dayType === "WORK",
    );
    const upcomingDays = workDays.filter(
      (entry) => String(entry.date).slice(0, 10) >= todayKey,
    );
    const skippedPast = workDays.length - upcomingDays.length;
    const workEntries = upcomingDays.map((entry) => {
      const hours =
        active.scope === "MODERATOR"
          ? getModeratorShiftHours(schedule.shift)
          : getShiftHours(schedule.shift, entry.date, branchShiftHours);
      return {
        employeeId: employee.employeeId,
        date: entry.date,
        dayType: "WORK",
        shift: schedule.shift,
        scheduledStartTime: hours.start,
        scheduledEndTime: hours.end,
        workHalfPeriod: schedule.workHalfPeriod || null,
      };
    });

    if (workEntries.length === 0) {
      setError(
        skippedPast
          ? "لا يمكن تطبيق المواعيد الجديدة على أيام انتهت — حتى لا تُحسب جزاءات بأثر رجعي"
          : "لا توجد أيام عمل لتحديث الفترة",
      );
      return;
    }

    setLoading(true);
    setError("");
    try {
      const updated = await updateTimesheet(active.id, { entries: workEntries });
      setActive(updated);
      const pastNote = skippedPast
        ? ` — ${skippedPast} يوم سابق لم يُغيَّر حتى لا تُحسب جزاءات بأثر رجعي`
        : "";
      setSuccess(
          updated.status === "DRAFT" && updated.requiresResubmit
            ? `تم حفظ التعديل — أرسل لإعادة الاعتماد${pastNote}`
            : `تم تطبيق الوردية على اليوم والأيام الجاية (مع مراعاة خميس/جمعة)${pastNote}`,
      );
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحديث الفترة"));
    } finally {
      setLoading(false);
    }
  }

  async function toggleRestDay(employeeId, date, currentlyRest, employee) {
    if (!active || !canEditEntries) return;
    if (!currentlyRest) {
      const blocked = restQuotaBlockReason(employee, date, timesheets, active);
      if (blocked) {
        setError(blocked);
        return;
      }
    }
    const rawFallback =
      schedulesByEmployee[employeeId]?.shift ||
      employee?.shift ||
      "MORNING";
    const fallbackShift =
      active.scope === "MODERATOR" && !MODERATOR_SHIFT_WINDOWS[rawFallback]
        ? "MORNING"
        : rawFallback;
    const hours =
      active.scope === "MODERATOR"
        ? getModeratorShiftHours(fallbackShift)
        : getShiftHours(fallbackShift, date, branchShiftHours);

    setLoading(true);
    setError("");
    try {
      const updated = await updateTimesheet(active.id, {
        entries: [
          currentlyRest
            ? {
                employeeId,
                date,
                dayType: "WORK",
                shift: fallbackShift,
                scheduledStartTime: hours.start,
                scheduledEndTime: hours.end,
              }
            : {
                employeeId,
                date,
                dayType: "REST",
              },
        ],
      });
      setActive(updated);
      if (updated.status === "DRAFT" && updated.requiresResubmit) {
        setSuccess("تم حفظ التعديل — أرسل لإعادة الاعتماد");
      }
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحديث اليوم"));
    } finally {
      setLoading(false);
    }
  }

  async function setAwayCoverageDay(employeeId, date) {
    if (!active || !canEditEntries) return;
    setLoading(true);
    setError("");
    try {
      const updated = await updateTimesheet(active.id, {
        entries: [
          {
            employeeId,
            date,
            dayType: "COVERAGE",
          },
        ],
      });
      setActive(updated);
      if (updated.status === "DRAFT" && updated.requiresResubmit) {
        setSuccess("تم حفظ التغطية — أرسل لإعادة الاعتماد");
      } else {
        setSuccess("تم تعليم اليوم كتغطية في فرع آخر");
      }
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحديث يوم التغطية"));
    } finally {
      setLoading(false);
    }
  }

  async function updateDayCell(employeeId, date, value, employee) {
    if (!active || !canEditEntries) return;
    const parsed = parseCellValue(value);
    if (parsed.dayType === "COVERAGE") {
      await setAwayCoverageDay(employeeId, date);
      return;
    }
    if (parsed.dayType === "REST") {
      if (active.scope === "INTERNAL_CONTROL") {
        await updateInternalControlDay(employeeId, date, { dayType: "REST" });
        return;
      }
      await toggleRestDay(employeeId, date, false, employee);
      return;
    }
    await updateDayShift(
      employeeId,
      date,
      parsed.shift,
      parsed.workHalfPeriod,
    );
  }

  async function handleSubmit() {
    if (!active) return;
    const wasAmendment = Boolean(active.requiresResubmit);
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const submitted = await submitTimesheet(active.id);
      setActive(submitted);
      setSuccess(
        isWarehouseManager || submitted?.scope === "WAREHOUSE"
          ? "تم اعتماد خطة المخزن الأسبوعية نهائياً"
          : isCreatorFinalTimesheetScope(submitted?.scope) ||
              isCashierManager ||
              isBranchManager
            ? "تم اعتماد الخطة الأسبوعية نهائياً. يمكنك تعديلها مباشرة بدون موافقة بعدك."
            : wasAmendment
              ? "تم إرسال التعديل لإعادة الاعتماد"
              : "تم إرسال الخطة الأسبوعية للاعتماد",
      );
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إرسال الخطة الأسبوعية"));
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    if (!active || active.status !== "DRAFT") return;
    if (!window.confirm("هل تريد حذف مسودة الخطة الأسبوعية؟ لا يمكن التراجع عن ذلك.")) {
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await deleteTimesheet(active.id);
      setActive(null);
      setSuccess("تم حذف مسودة الخطة الأسبوعية");
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حذف الخطة الأسبوعية"));
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel() {
    if (!active || !canCreate) return;
    const isCreatorFinal = isCreatorFinalTimesheetScope(active.scope);
    if (isCreatorFinal) {
      if (active.status !== "APPROVED") return;
    } else if (active.status !== "PENDING") {
      return;
    }
    if (
      !window.confirm(
        isCreatorFinal
          ? "هل تريد إلغاء الخطة الأسبوعية المعتمدة؟ يمكنك إنشاء خطة جديدة لنفس الأسبوع بعد الإلغاء."
          : "هل تريد إلغاء الخطة الأسبوعية قبل الاعتماد؟ يمكنك إنشاء خطة أسبوعية جديدة لنفس الأسبوع بعد الإلغاء.",
      )
    ) {
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const updated = await cancelTimesheet(active.id);
      setActive(updated);
      setSuccess(
        isCreatorFinal
          ? "تم إلغاء الخطة الأسبوعية. يمكنك إنشاء خطة جديدة لنفس الأسبوع."
          : "تم إلغاء الخطة الأسبوعية. يمكنك إنشاء خطة أسبوعية جديدة لنفس الأسبوع.",
      );
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إلغاء الخطة الأسبوعية"));
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove() {
    if (!active || !canApproveActive) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const updated = await approveTimesheet(active.id, {});
      setActive(updated);
      setSuccess(
        updated.status === "APPROVED"
          ? "تم اعتماد الخطة الأسبوعية نهائياً"
          : "تمت الموافقة — بانتظار اعتماد الخطوة التالية",
      );
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر اعتماد الخطة الأسبوعية"));
    } finally {
      setLoading(false);
    }
  }

  async function handleReject() {
    if (!active || !canApproveActive) return;
    const notes = window.prompt("سبب الرفض (مطلوب):");
    if (notes === null) return;
    if (!String(notes).trim()) {
      setError("يجب كتابة سبب الرفض");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const updated = await rejectTimesheet(active.id, {
        notes: String(notes).trim(),
      });
      setActive(updated);
      setSuccess("تم رفض الخطة الأسبوعية");
      await loadTimesheets();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر رفض الخطة الأسبوعية"));
    } finally {
      setLoading(false);
    }
  }

  function openCoverageItem(plan, branchId) {
    if (plan) {
      setActive(plan);
      return;
    }
    if (!isCashierManager || !branchId) return;
    setSelectedBranchId(branchId);
    setSelectedIds([]);
    setSchedulesByEmployee({});
    const createEl = document.getElementById("timesheet-create");
    createEl?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  useEffect(() => {
    if (!active?.employees) return;
    const morningDefaults = getShiftHours("MORNING", null, branchShiftHours);
    setSchedulesByEmployee((prev) => {
      const next = { ...prev };
      for (const emp of active.employees) {
        if (!next[emp.employeeId] && emp.shift) {
          next[emp.employeeId] = {
            shift: emp.shift,
            scheduledStartTime:
              emp.scheduledStartTime || morningDefaults.start,
            scheduledEndTime: emp.scheduledEndTime || morningDefaults.end,
          };
        }
      }
      return next;
    });
  }, [active, branchShiftHours]);

  if (!canAccessTimesheet) {
    return (
      <div className="branch-timesheet-page" dir="rtl">
        <div className="alert alert-warning">
          الخطة متاحة لمدير الفرع ومدير الكاشيرية ومدير التسويق وأمين المخزن
          ومديري المنطقة/التشغيل والحسابات والموارد البشرية
        </div>
      </div>
    );
  }

  return (
    <div className="branch-timesheet-page" dir="rtl">
      <div className="ess-home-header">
        <h1 className="ess-home-title">
          {isWarehouseManager
            ? "خطة المخزن الأسبوعية"
            : isInternalControlMember
              ? "الخطط الأسبوعية للفروع"
              : "الخطة الأسبوعية"}
        </h1>
      </div>

      <div className="timesheet-week-bar card mb-3">
          <div className="card-body py-2 d-flex flex-wrap align-items-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() =>
                setWeekOf((prev) => addUtcDays(selectedWeekRange.weekStart || prev, -7))
              }
              disabled={loading}
            >
              الأسبوع السابق
            </button>
            <div className="timesheet-week-bar__picker">
              <label className="form-label mb-1" htmlFor="ts-week-bar">
                أسبوع الخطة (سبت–جمعة)
              </label>
              <input
                id="ts-week-bar"
                type="date"
                className="form-control"
                value={weekOf}
                onChange={(e) => setWeekOf(e.target.value)}
                disabled={loading}
              />
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() =>
                setWeekOf((prev) => addUtcDays(selectedWeekRange.weekStart || prev, 7))
              }
              disabled={loading}
            >
              الأسبوع التالي
            </button>
            {selectedWeekRange.weekStart ? (
              <div className="timesheet-week-bar__range">
                {formatDisplayDate(selectedWeekRange.weekStart)} →{" "}
                {formatDisplayDate(selectedWeekRange.weekEnd)}
              </div>
            ) : null}
          </div>
        </div>

      {missingPlanWarning ? (
        <div className="timesheet-week-alert" role="alert">
          <p className="timesheet-week-alert__title">{missingPlanWarning.title}</p>
          {missingPlanWarning.dates ? (
            <p className="timesheet-week-alert__dates">{missingPlanWarning.dates}</p>
          ) : null}
        </div>
      ) : null}

      {showHqCoverageBoard ? (
        <div className="timesheet-coverage-board mb-3">
          <div className="timesheet-coverage-head">
            <h2 className="timesheet-coverage-title">
              خطط المقر — الموديريتور والمخزن
              {selectedWeekRange.weekStart
                ? ` — ${formatDisplayDate(selectedWeekRange.weekStart)} → ${formatDisplayDate(selectedWeekRange.weekEnd)}`
                : ""}
            </h2>
            <div className="timesheet-coverage-counts">
              <span className="timesheet-coverage-count timesheet-coverage-count--missing">
                تحتاج خطة: {hqCoverage.missing.length}
              </span>
              <span className="timesheet-coverage-count timesheet-coverage-count--ready">
                اتعملها خطة: {hqCoverage.ready.length}
              </span>
            </div>
          </div>
          <div className="timesheet-coverage-grid">
            <section className="timesheet-coverage-col timesheet-coverage-col--missing">
              <h3>محتاجة خطة أسبوعية</h3>
              {hqCoverage.missing.length === 0 ? (
                <p className="timesheet-coverage-empty">
                  خطط الموديريتور والمخزن موجودة لهذا الأسبوع
                </p>
              ) : (
                <ul className="timesheet-coverage-list">
                  {hqCoverage.missing.map((row) => {
                    const chip = coverageChip(row.plan);
                    return (
                      <li key={row.id}>
                        <div className="timesheet-coverage-item">
                          <span className="timesheet-coverage-name">
                            {row.name}
                          </span>
                          <span className="timesheet-coverage-chips">
                            <span
                              className={`timesheet-cov-chip timesheet-cov-chip--${chip.tone}`}
                            >
                              {row.scopeLabel}: {chip.label}
                            </span>
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
            <section className="timesheet-coverage-col timesheet-coverage-col--ready">
              <h3>اتعمل لها خطة</h3>
              {hqCoverage.ready.length === 0 ? (
                <p className="timesheet-coverage-empty">
                  لا توجد خطة موديريتور أو مخزن لهذا الأسبوع
                </p>
              ) : (
                <ul className="timesheet-coverage-list">
                  {hqCoverage.ready.map((row) => {
                    const chip = coverageChip(row.plan);
                    const isCurrent = active?.id === row.plan?.id;
                    return (
                      <li key={row.id}>
                        <button
                          type="button"
                          className={`timesheet-coverage-item${
                            isCurrent
                              ? " timesheet-coverage-item--current"
                              : ""
                          }`}
                          onClick={() => {
                            if (row.plan) setActive(row.plan);
                          }}
                        >
                          <span className="timesheet-coverage-name">
                            {row.name}
                          </span>
                          <span className="timesheet-coverage-chips">
                            <span
                              className={`timesheet-cov-chip timesheet-cov-chip--${chip.tone}`}
                            >
                              {row.scopeLabel}: {chip.label}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        </div>
      ) : null}

      {showBranchCoverageBoard ? (
        <div className="timesheet-coverage-board mb-3">
          <div className="timesheet-coverage-head">
            <h2 className="timesheet-coverage-title">
              حالة خطط الفروع
              {selectedWeekRange.weekStart
                ? ` — ${formatDisplayDate(selectedWeekRange.weekStart)} → ${formatDisplayDate(selectedWeekRange.weekEnd)}`
                : ""}
            </h2>
            <div className="timesheet-coverage-counts">
              <span className="timesheet-coverage-count timesheet-coverage-count--missing">
                تحتاج خطة: {branchCoverage.missing.length}
              </span>
              <span className="timesheet-coverage-count timesheet-coverage-count--ready">
                اتعملها خطة: {branchCoverage.ready.length}
              </span>
            </div>
          </div>
          <div className="timesheet-coverage-grid">
            <section className="timesheet-coverage-col timesheet-coverage-col--missing">
              <h3>فروع محتاجة خطة أسبوعية</h3>
              {branchCoverage.missing.length === 0 ? (
                <p className="timesheet-coverage-empty">
                  كل الفروع عليها خطة لهذا الأسبوع
                </p>
              ) : (
                <ul className="timesheet-coverage-list">
                  {branchCoverage.missing.map((row) => {
                    const opsChip = isCashierManager ? null : coverageChip(row.ops);
                    const cashierChip = coverageChip(row.cashier);
                    const icChip = isInternalControlMember
                      ? coverageChip(row.ic)
                      : null;
                    const openPlan = isCashierManager
                      ? row.cashier
                      : isInternalControlMember
                        ? row.ic || row.ops || row.cashier
                        : row.cashier || row.ops;
                    const isCurrent =
                      row.branch.id === selectedBranchId ||
                      row.branch.id === active?.branchId;
                    return (
                      <li key={row.branch.id}>
                        <button
                          type="button"
                          className={`timesheet-coverage-item${
                            isCurrent ? " timesheet-coverage-item--current" : ""
                          }`}
                          onClick={() =>
                            openCoverageItem(openPlan, row.branch.id)
                          }
                        >
                          <span className="timesheet-coverage-name">
                            {row.branch.name}
                            {row.branch.code ? (
                              <small> ({row.branch.code})</small>
                            ) : null}
                          </span>
                          <span className="timesheet-coverage-chips">
                            {opsChip ? (
                              <span
                                className={`timesheet-cov-chip timesheet-cov-chip--${opsChip.tone}`}
                              >
                                تشغيل: {opsChip.label}
                              </span>
                            ) : null}
                            <span
                              className={`timesheet-cov-chip timesheet-cov-chip--${cashierChip.tone}`}
                            >
                              {isCashierManager
                                ? cashierChip.label
                                : `كاشيرية: ${cashierChip.label}`}
                            </span>
                            {icChip ? (
                              <span
                                className={`timesheet-cov-chip timesheet-cov-chip--${icChip.tone}`}
                              >
                                رقابة: {icChip.label}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
            <section className="timesheet-coverage-col timesheet-coverage-col--ready">
              <h3>فروع اتعملها خطة</h3>
              {branchCoverage.ready.length === 0 ? (
                <p className="timesheet-coverage-empty">
                  مفيش فرع مكتمل الخطة لهذا الأسبوع
                </p>
              ) : (
                <ul className="timesheet-coverage-list">
                  {branchCoverage.ready.map((row) => {
                    const opsChip = isCashierManager ? null : coverageChip(row.ops);
                    const cashierChip = coverageChip(row.cashier);
                    const icChip = isInternalControlMember
                      ? coverageChip(row.ic)
                      : null;
                    const openPlan = isCashierManager
                      ? row.cashier
                      : isInternalControlMember
                        ? row.ic || row.ops || row.cashier
                        : row.ops || row.cashier;
                    const isCurrent =
                      row.branch.id === selectedBranchId ||
                      row.branch.id === active?.branchId;
                    return (
                      <li key={row.branch.id}>
                        <button
                          type="button"
                          className={`timesheet-coverage-item${
                            isCurrent ? " timesheet-coverage-item--current" : ""
                          }`}
                          onClick={() =>
                            openCoverageItem(openPlan, row.branch.id)
                          }
                        >
                          <span className="timesheet-coverage-name">
                            {row.branch.name}
                            {row.branch.code ? (
                              <small> ({row.branch.code})</small>
                            ) : null}
                          </span>
                          <span className="timesheet-coverage-chips">
                            {opsChip ? (
                              <span
                                className={`timesheet-cov-chip timesheet-cov-chip--${opsChip.tone}`}
                              >
                                تشغيل: {opsChip.label}
                              </span>
                            ) : null}
                            <span
                              className={`timesheet-cov-chip timesheet-cov-chip--${cashierChip.tone}`}
                            >
                              {isCashierManager
                                ? cashierChip.label
                                : `كاشيرية: ${cashierChip.label}`}
                            </span>
                            {icChip ? (
                              <span
                                className={`timesheet-cov-chip timesheet-cov-chip--${icChip.tone}`}
                              >
                                رقابة: {icChip.label}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        </div>
      ) : null}

      {(canCreate || canEditEntries) &&
      !isInternalControlManager &&
      active?.scope !== "INTERNAL_CONTROL" ? (
        <p className="timesheet-manager-note" role="note">
          {WEEKLY_REST_QUOTA_NOTE}
        </p>
      ) : null}
      {isHrPlanCreator ? (
        <p className="timesheet-manager-note" role="note">
          ابحث واختر أي موظف — حتى لو مش من الفرع أو من الإدارة. الخطة بتتبني
          على فرع الموظف، أو على فرع العمل لو اخترته. لو الخطة موجودة لنفس
          الفرع والأسبوع، الموظف بيتضاف عليها. مواعيد حرة من–إلى.
        </p>
      ) : null}
      {isHrOverview && !canCreate ? (
        <p className="timesheet-manager-note" role="note">
          خطط الموديريتور والمخزن تظهر هنا للعرض فقط، زي خطط الفروع. الاعتماد
          النهائي من منشئ الخطة.
        </p>
      ) : null}
      {isCashierManager ? (
        <p className="timesheet-manager-note" role="note">
          يوم الجرد: الكاشير يبصم في أي وقت، 8 ساعات محسوبة، والزيادة أوفر تايم.
          لو الكاشير مش في الفرع ده وشغال في فرع تاني نفس اليوم، اختار «تغطية (فرع آخر)».
          التغطية ليوم أو أكثر، وينفع نص شيفت في فرع والنص التاني في فرع تاني.
          الخطة تعتمدها أنت نهائياً، ومفيش موافقة بعدك.
        </p>
      ) : null}
      {isBranchManager ? (
        <p className="timesheet-manager-note" role="note">
          سجّلوا أيام العمل والراحات الأسبوعية فقط، ولا تسجّلوا أيام الإجازة.
          الراحة الأسبوعية 4 أيام في الأسبوع تتحسب راحة عادي، والحد الشهري 4 أيام
          افتراضياً (أو الحد اللي تخصّصه الموارد البشرية للموظف).
          لو الموظف مش في الفرع ده وشغال في فرع تاني نفس اليوم، اختار «تغطية (فرع آخر)» — مش حضور هنا ومش راحة.
          التغطية من فرع آخر ممكن ليوم أو أكثر، ونص شيفت في فرع والنص التاني في فرع تاني.
          الخطة تعتمدها أنت نهائياً، ومفيش موافقة بعدك (لا مدير منطقة ولا HR).
        </p>
      ) : null}
      {isWarehouseManager ? (
        <p className="timesheet-manager-note" role="note">
          أنشئ خطة أسبوعية لموظفي المخازن بمواعيد حرة (أي من–إلى، حتى لو بره
          مواعيد النظام). 9–7 و 9–6 اختصارات فقط، مع بريك أو بدونه. اعتمدها
          نهائياً؛ مفيش موافقة بعدك (ولا مدير إدارة المخازن).
        </p>
      ) : null}
      {isModeratorLeader ? (
        <p className="timesheet-manager-note" role="note">
          أنشئ الخطة الأسبوعية لفريق الموديريتور بمواعيد 24 ساعة:
          00:00–08:00 موظف واحد، 08:00–16:00 موظف واحد، 16:00–00:00 موظفان.
          إنتي تعتمديها نهائياً ومفيش موافقة بعدك. باقي الطلبات (إجازة وإذن)
          تمشي الدورة العادية لحد مدير الإدارة.
        </p>
      ) : null}
      {isInternalControlManager ? (
        <p className="timesheet-manager-note" role="note">
          عيّن لكل موظف: الفرع + اليوم + من/إلى. نفس الموظف يقدر يراقب فروع
          مختلفة في أيام مختلفة، ومواعيد كل يوم مستقلة (صبح أو ليل). ممنوع فرعين
          في نفس اليوم. خطط التشغيل والكاشيرية لكل الفروع تظهر هنا للعرض فقط.
        </p>
      ) : null}
      {isInternalControlMember && !isInternalControlManager ? (
        <p className="timesheet-manager-note" role="note">
          اعرض الخطط الأسبوعية لكل الفروع — تشغيل وكاشيرية ورقابة داخلية.
        </p>
      ) : null}

      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      ) : null}

      {canCreate && isInternalControlManager ? (
        <div className="card mb-3" id="timesheet-create">
          <div className="card-body">
            <h2 className="h5 mb-3">تعيين مراقبة كاميرات (يوم + فرع + مواعيد)</h2>
            <p className="text-muted small mb-3">
              أضف صفاً لكل يوم عمل. الموظف الواحد يقدر يتوزع على فروع مختلفة
              بأيام مختلفة وبمواعيد مختلفة.
            </p>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>الموظف</th>
                    <th>الفرع</th>
                    <th>اليوم</th>
                    <th>من</th>
                    <th>إلى</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {icAssignRows.map((row, idx) => (
                    <tr key={row.key}>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={row.employeeId}
                          disabled={loading}
                          onChange={(e) =>
                            setIcAssignRows((prev) =>
                              prev.map((r, i) =>
                                i === idx
                                  ? { ...r, employeeId: e.target.value }
                                  : r,
                              ),
                            )
                          }
                        >
                          <option value="">اختر</option>
                          {employeesForBranch.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.fullName || emp.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={row.branchId}
                          disabled={loading}
                          onChange={(e) =>
                            setIcAssignRows((prev) =>
                              prev.map((r, i) =>
                                i === idx
                                  ? { ...r, branchId: e.target.value }
                                  : r,
                              ),
                            )
                          }
                        >
                          <option value="">اختر</option>
                          {availableBranches.map((branch) => (
                            <option key={branch.id} value={branch.id}>
                              {branch.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={row.date}
                          min={selectedWeekRange.weekStart || undefined}
                          max={selectedWeekRange.weekEnd || undefined}
                          disabled={loading}
                          onChange={(e) =>
                            setIcAssignRows((prev) =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, date: e.target.value } : r,
                              ),
                            )
                          }
                        />
                      </td>
                      <td>
                        <HhMmInput
                          value={row.scheduledStartTime}
                          disabled={loading}
                          aria-label="من"
                          onChange={(e) =>
                            setIcAssignRows((prev) =>
                              prev.map((r, i) =>
                                i === idx
                                  ? {
                                      ...r,
                                      scheduledStartTime: e.target.value,
                                    }
                                  : r,
                              ),
                            )
                          }
                        />
                      </td>
                      <td>
                        <HhMmInput
                          value={row.scheduledEndTime}
                          disabled={loading}
                          aria-label="إلى"
                          onChange={(e) =>
                            setIcAssignRows((prev) =>
                              prev.map((r, i) =>
                                i === idx
                                  ? { ...r, scheduledEndTime: e.target.value }
                                  : r,
                              ),
                            )
                          }
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          disabled={loading}
                          onClick={() =>
                            setIcAssignRows((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                        >
                          حذف
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={loading}
                onClick={() =>
                  setIcAssignRows((prev) => [
                    ...prev,
                    {
                      key: `ic-${Date.now()}-${prev.length}`,
                      employeeId: "",
                      branchId: selectedBranchId || "",
                      date: selectedWeekRange.weekStart || weekOf,
                      scheduledStartTime: "09:00",
                      scheduledEndTime: "17:00",
                    },
                  ])
                }
              >
                إضافة يوم مراقبة
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={loading || icAssignRows.length === 0}
                onClick={handleAssignIcWatches}
              >
                اعتماد التعيينات
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {canCreate && !isInternalControlManager ? (
        <div className="card mb-3" id="timesheet-create">
          <div className="card-body">
            <h2 className="h5 mb-3">إنشاء مسودة أسبوع جديد</h2>
            <div className="row g-3 align-items-end">
              {isCashierManager ? (
                <div className="col-md-4">
                  <label className="form-label" htmlFor="ts-branch">
                    فرع العمل (مكان التغطية)
                  </label>
                  <select
                    id="ts-branch"
                    className="form-select"
                    value={selectedBranchId}
                    onChange={(e) => {
                      setSelectedBranchId(e.target.value);
                      setSelectedIds([]);
                      setSchedulesByEmployee({});
                    }}
                    disabled={loading}
                  >
                    <option value="">اختر الفرع</option>
                    {availableBranches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                        {branch.code ? ` (${branch.code})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {isHrPlanCreator ? (
                <div className="col-md-4">
                  <label className="form-label" htmlFor="ts-branch-hr">
                    فرع العمل (اختياري — للتغطية)
                  </label>
                  <select
                    id="ts-branch-hr"
                    className="form-select"
                    value={selectedBranchId}
                    onChange={(e) => setSelectedBranchId(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">بدون فرع — استخدم فرع الموظف</option>
                    {availableBranches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                        {branch.code ? ` (${branch.code})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="col-md-4">
                <label className="form-label" htmlFor="ts-week">
                  أي يوم داخل الأسبوع (سبت–جمعة)
                </label>
                <input
                  id="ts-week"
                  type="date"
                  className="form-control"
                  value={weekOf}
                  onChange={(e) => setWeekOf(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="col-md-4">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreate}
                  disabled={loading || selectedIds.length === 0}
                >
                  إنشاء مسودة للموظفين المحددين
                </button>
              </div>
            </div>

            <div className="mt-3">
              <div className="fw-semibold mb-2">
                {isWarehouseManager
                  ? "موظفو إدارة المخازن"
                  : isHrPlanCreator
                    ? "اختر موظفين — مواعيد حرة من–إلى"
                  : isModeratorLeader
                    ? "فريق الموديريتور"
                    : isMarketingManager
                    ? "موظفو قسم التسويق"
                    : isCashierManager
                      ? "كل الكاشيرات — حدد مين يشتغل في فرع العمل ده"
                      : "موظفو الفرع + تغطية من فروع أخرى (بدون الكاشيرية)"}
              </div>
              {isHrPlanCreator ? (
                <div className="mb-3">
                  <label className="form-label" htmlFor="ts-hr-emp-search">
                    بحث عن موظف
                  </label>
                  <input
                    id="ts-hr-emp-search"
                    type="search"
                    className="form-control"
                    value={hrEmployeeSearch}
                    onChange={(e) => setHrEmployeeSearch(e.target.value)}
                    placeholder="اكتب الاسم أو الكود..."
                    disabled={loading}
                    autoComplete="off"
                  />
                  <div className="form-text">
                    {hrEmployeeSearch.trim()
                      ? `${hrEmployeesVisible.length} نتيجة`
                      : "ابحث بالاسم أو الكود — أي موظف حتى لو مش من الفرع"}
                    {selectedIds.length
                      ? ` — محدد: ${selectedIds.length}`
                      : ""}
                  </div>
                </div>
              ) : null}
              <div className="timesheet-emp-grid">
                {(isHrPlanCreator ? hrEmployeesVisible : employeesForBranch).map(
                  (emp) => {
                  const selected = selectedIds.includes(emp.id);
                  if (isWarehouseManager || isHrPlanCreator) {
                    const schedule =
                      schedulesByEmployee[emp.id] ||
                      defaultWarehouseSchedule();
                    return (
                      <div
                        key={emp.id}
                        className={`timesheet-emp-card ${selected ? "selected" : ""}`}
                      >
                        <label className="d-flex gap-2 align-items-start">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              setSelectedIds((prev) =>
                                selected
                                  ? prev.filter((id) => id !== emp.id)
                                  : [...prev, emp.id],
                              );
                              if (!selected && !schedulesByEmployee[emp.id]) {
                                setSchedulesByEmployee((prev) => ({
                                  ...prev,
                                  [emp.id]: defaultWarehouseSchedule(),
                                }));
                              }
                            }}
                            disabled={loading}
                          />
                          <span>
                            <strong>{emp.fullName || emp.name}</strong>
                            {emp.employeeCode || emp.code ? (
                              <span className="text-muted">
                                {" "}
                                ({emp.employeeCode || emp.code})
                              </span>
                            ) : null}
                            {isHrPlanCreator ? (
                              <div className="small text-muted">
                                {employeePlaceLabel(emp)}
                              </div>
                            ) : null}
                          </span>
                        </label>
                        {selected ? (
                          <div className="mt-2 row g-2">
                            <div className="col-12 d-flex flex-wrap gap-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={loading}
                                onClick={() =>
                                  setSchedulesByEmployee((prev) => ({
                                    ...prev,
                                    [emp.id]: {
                                      ...schedule,
                                      scheduledStartTime:
                                        warehouseDefaults.start || "09:00",
                                      scheduledEndTime:
                                        warehouseDefaults.end || "19:00",
                                    },
                                  }))
                                }
                              >
                                {`${(warehouseDefaults.start || "09:00").slice(0, 5)}–${(warehouseDefaults.end || "19:00").slice(0, 5)}`}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={loading}
                                onClick={() =>
                                  setSchedulesByEmployee((prev) => ({
                                    ...prev,
                                    [emp.id]: {
                                      ...schedule,
                                      scheduledStartTime: "09:00",
                                      scheduledEndTime: "18:00",
                                    },
                                  }))
                                }
                              >
                                9–6
                              </button>
                              {customShiftPresets.map((preset) => {
                                const hours = getCustomPresetHours(
                                  preset,
                                  null,
                                );
                                if (!hours) return null;
                                return (
                                  <button
                                    key={preset.id}
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={loading}
                                    title={`${hours.start}–${hours.end}`}
                                    onClick={() =>
                                      setSchedulesByEmployee((prev) => ({
                                        ...prev,
                                        [emp.id]: {
                                          ...schedule,
                                          scheduledStartTime: hours.start,
                                          scheduledEndTime: hours.end,
                                        },
                                      }))
                                    }
                                  >
                                    {preset.nameAr}
                                  </button>
                                );
                              })}
                              <span className="small text-muted align-self-center">
                                أو اكتب أي من–إلى
                              </span>
                            </div>
                            <div className="col-6">
                              <label className="form-label small mb-0">من</label>
                              <HhMmInput
                                value={schedule.scheduledStartTime || "09:00"}
                                onChange={(e) =>
                                  setSchedulesByEmployee((prev) => ({
                                    ...prev,
                                    [emp.id]: {
                                      ...schedule,
                                      scheduledStartTime: e.target.value,
                                    },
                                  }))
                                }
                                disabled={loading}
                                aria-label="من"
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label small mb-0">إلى</label>
                              <HhMmInput
                                value={schedule.scheduledEndTime || "19:00"}
                                onChange={(e) =>
                                  setSchedulesByEmployee((prev) => ({
                                    ...prev,
                                    [emp.id]: {
                                      ...schedule,
                                      scheduledEndTime: e.target.value,
                                    },
                                  }))
                                }
                                disabled={loading}
                                aria-label="إلى"
                              />
                            </div>
                            <div className="col-12">
                              <label className="form-check small mb-0">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={Boolean(schedule.hasBreak)}
                                  onChange={(e) =>
                                    setSchedulesByEmployee((prev) => ({
                                      ...prev,
                                      [emp.id]: {
                                        ...schedule,
                                        hasBreak: e.target.checked,
                                      },
                                    }))
                                  }
                                  disabled={loading}
                                />
                                <span className="form-check-label">بريك ساعة</span>
                              </label>
                            </div>
                            {isHrPlanCreator &&
                            selectedBranchId &&
                            (emp.branchId || emp.branch?.id) &&
                            (emp.branchId || emp.branch?.id) !==
                              selectedBranchId ? (
                              <div className="col-12 timesheet-coverage-days">
                                <div className="form-label small mb-1">
                                  أيام التغطية في فرع العمل
                                </div>
                                <div className="timesheet-coverage-day-list">
                                  {listWeekDates(weekOf).map((date, idx) => {
                                    const checked = (
                                      schedule.coverageDates || []
                                    ).includes(date);
                                    return (
                                      <label
                                        key={date}
                                        className="timesheet-coverage-day"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() =>
                                            toggleCoverageDate(emp.id, date)
                                          }
                                          disabled={loading}
                                        />
                                        <span>
                                          {DAY_LABELS[idx] || date}
                                          <small>
                                            {" "}
                                            {formatDisplayDate(date)}
                                          </small>
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                                <div className="form-text">
                                  اختياري — لو ما تحددش أيام، الأسبوع كله على
                                  فرع العمل
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  }
                  if (isModeratorLeader) {
                    const schedule =
                      schedulesByEmployee[emp.id] ||
                      defaultModeratorSchedule();
                    return (
                      <div
                        key={emp.id}
                        className={`timesheet-emp-card ${selected ? "selected" : ""}`}
                      >
                        <label className="d-flex gap-2 align-items-start">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              setSelectedIds((prev) =>
                                selected
                                  ? prev.filter((id) => id !== emp.id)
                                  : [...prev, emp.id],
                              );
                              if (!selected && !schedulesByEmployee[emp.id]) {
                                setSchedulesByEmployee((prev) => ({
                                  ...prev,
                                  [emp.id]: defaultModeratorSchedule(),
                                }));
                              }
                            }}
                            disabled={loading}
                          />
                          <span>
                            <strong>{emp.fullName || emp.name}</strong>
                            {emp.employeeCode || emp.code ? (
                              <span className="text-muted">
                                {" "}
                                ({emp.employeeCode || emp.code})
                              </span>
                            ) : null}
                          </span>
                        </label>
                        {selected ? (
                          <div className="mt-2 d-flex flex-wrap gap-1">
                            {Object.entries(MODERATOR_SHIFT_WINDOWS).map(
                              ([shift, window]) => (
                                <button
                                  key={shift}
                                  type="button"
                                  className={`btn btn-sm ${
                                    schedule.shift === shift
                                      ? "btn-primary"
                                      : "btn-outline-secondary"
                                  }`}
                                  disabled={loading}
                                  onClick={() =>
                                    setSchedulesByEmployee((prev) => ({
                                      ...prev,
                                      [emp.id]: {
                                        shift,
                                        scheduledStartTime: window.start,
                                        scheduledEndTime: window.end,
                                        hasBreak: false,
                                      },
                                    }))
                                  }
                                >
                                  {window.label}
                                </button>
                              ),
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  }
                  const schedule =
                    schedulesByEmployee[emp.id] ||
                    defaultSchedule(branchShiftHours);
                  const homeId = emp.branchId || emp.branch?.id || "";
                  const workId = branchIdForCreate;
                  const isCoverage = Boolean(
                    workId && homeId && homeId !== workId,
                  );
                  const homeName = emp.branch?.name || "";
                  const weekDates = listWeekDates(weekOf);
                  return (
                    <div
                      key={emp.id}
                      className={`timesheet-emp-card${isCoverage ? " timesheet-emp-card--coverage" : ""}`}
                    >
                      <label className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selected}
                          onChange={() => toggleEmployee(emp.id)}
                          disabled={loading}
                        />
                        <span className="form-check-label">
                          {emp.fullName}{" "}
                          <small className="text-muted">
                            ({emp.employeeCode}
                            {emp.jobTitle ? ` — ${emp.jobTitle}` : ""})
                          </small>
                          {homeName ? (
                            <span className="timesheet-home-branch">
                              {" "}
                              أصلي: {homeName}
                            </span>
                          ) : null}
                          {isCoverage ? (
                            <span className="badge bg-warning text-dark timesheet-coverage-badge">
                              تغطية
                            </span>
                          ) : null}
                        </span>
                      </label>
                      {selected ? (
                        <div className="timesheet-shift-fields">
                          {isCoverage ? (
                            <div className="mb-2 timesheet-coverage-days">
                              <div className="form-label small mb-1">
                                أيام التغطية
                              </div>
                              <div className="timesheet-coverage-day-list">
                                {weekDates.map((date, idx) => {
                                  const checked = (
                                    schedule.coverageDates || []
                                  ).includes(date);
                                  return (
                                    <label
                                      key={date}
                                      className="timesheet-coverage-day"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                          toggleCoverageDate(emp.id, date)
                                        }
                                        disabled={loading}
                                      />
                                      <span>
                                        {DAY_LABELS[idx] || date}
                                        <small>
                                          {" "}
                                          {formatDisplayDate(date)}
                                        </small>
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                              <div className="form-text">
                                يوم أو أكثر — باقي الأسبوع على الفرع الأصلي
                              </div>
                            </div>
                          ) : null}
                          <select
                            className="form-select form-select-sm"
                            value={schedule.shift}
                            onChange={(e) =>
                              updateSchedule(emp.id, { shift: e.target.value })
                            }
                            disabled={loading}
                            aria-label="الفترة الافتراضية"
                          >
                            <option value="MORNING">
                              {shiftOptionLabel("MORNING", branchShiftHours)}
                            </option>
                            <option value="EVENING">
                              {shiftOptionLabel("EVENING", branchShiftHours)}
                            </option>
                            {isCashierManager ? (
                              <option value="BOTH">
                                {shiftOptionLabel("BOTH", branchShiftHours)}
                              </option>
                            ) : null}
                            {isCashierManager ? (
                              <option value="INVENTORY">
                                جرد (8 ساعات — أي وقت — الزيادة أوفر تايم)
                              </option>
                            ) : null}
                          </select>
                          {customShiftPresets.length > 0 &&
                          schedule.shift !== "INVENTORY" ? (
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {customShiftPresets.map((preset) => {
                                const hours = getCustomPresetHours(
                                  preset,
                                  null,
                                );
                                if (!hours) return null;
                                return (
                                  <button
                                    key={preset.id}
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={loading}
                                    title={`تطبيق ${hours.start}–${hours.end} كصباحي`}
                                    onClick={() =>
                                      updateSchedule(emp.id, {
                                        shift: "MORNING",
                                        scheduledStartTime: hours.start,
                                        scheduledEndTime: hours.end,
                                      })
                                    }
                                  >
                                    {preset.nameAr}
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                          {schedule.shift !== "INVENTORY" ? (
                            <select
                              className="form-select form-select-sm mt-1"
                              value={schedule.workHalfPeriod || ""}
                              onChange={(e) =>
                                updateSchedule(emp.id, {
                                  workHalfPeriod: e.target.value,
                                })
                              }
                              disabled={loading}
                              aria-label="نص الشيفت"
                            >
                              <option value="">شيفت كامل في الفرع ده</option>
                              <option value="FIRST_HALF">
                                النص الأول في الفرع ده
                              </option>
                              <option value="SECOND_HALF">
                                النص التاني في الفرع ده
                              </option>
                            </select>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {(isHrPlanCreator
                  ? hrEmployeesVisible
                  : employeesForBranch
                ).length === 0 ? (
                  <div className="text-muted">
                    {isHrPlanCreator
                      ? hrEmployeeSearch.trim()
                        ? "لا توجد نتائج مطابقة للبحث"
                        : "ابحث بالاسم أو الكود — حتى لو الموظف مش من الفرع"
                      : isCashierManager
                      ? selectedBranchId
                        ? "لا يوجد كاشير نشط في الحسابات"
                        : "اختر فرع العمل أولاً"
                      : isWarehouseManager
                        ? "لا يوجد موظفون في إدارة المخازن"
                        : isModeratorLeader
                          ? "لا يوجد موظفون في فريق الموديريتور"
                        : isMarketingManager
                        ? "لا يوجد موظفون في قسم التسويق"
                        : "لا يوجد موظفون في الفرع (بدون الكاشيرية)"}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="row g-3">
        <div className="col-lg-3">
          <div className="card">
            <div className="card-header">
              {isWarehouseManager
                ? "خطط المخزن الأسبوعية"
                : selectedWeekRange.weekStart
                  ? `خطط الأسبوع ${formatDisplayDate(selectedWeekRange.weekStart)}`
                  : "الخطط الأسبوعية"}
            </div>
            <ul className="list-group list-group-flush">
              {timesheetsForWeek.map((ts) => (
                <button
                  type="button"
                  key={ts.id}
                  className={`list-group-item list-group-item-action text-end ${
                    active?.id === ts.id ? "active" : ""
                  }`}
                  onClick={() => setActive(ts)}
                >
                  <div className="d-flex justify-content-between align-items-center gap-2">
                    <span
                      className={`badge ${STATUS_BADGE[ts.status] || "bg-secondary"}`}
                    >
                      {STATUS_LABELS[ts.status] || ts.status}
                    </span>
                    <span>
                      {ts.scope === "WAREHOUSE"
                        ? `${formatDisplayDate(ts.weekStartDate)} → ${formatDisplayDate(ts.weekEndDate)}`
                        : `${ts.weekStartDate} → ${ts.weekEndDate}`}
                    </span>
                  </div>
                  <small>
                    {ts.scope === "CASHIER"
                      ? "كاشيرية — "
                      : ts.scope === "DEPARTMENT"
                        ? "تسويق — "
                        : ts.scope === "MODERATOR"
                          ? "موديريتور — "
                        : ts.scope === "WAREHOUSE"
                          ? "مخزن — "
                          : ts.scope === "INTERNAL_CONTROL"
                            ? "رقابة داخلية — "
                            : ""}
                    {ts.branch?.name ||
                      ts.department?.name ||
                      ts.branchId ||
                      ts.departmentId}
                  </small>
                </button>
              ))}
              {timesheetsForWeek.length === 0 ? (
                <li className="list-group-item text-muted">
                  {isWarehouseManager
                    ? "لا توجد خطط مخزن لهذا الأسبوع"
                    : "لا توجد خطط أسبوعية لهذا الأسبوع"}
                </li>
              ) : null}
            </ul>
          </div>
        </div>

        <div className="col-lg-9">
          {active ? (
            <div className="card timesheet-plan-card">
              <div className="card-body p-2 p-md-3">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <div>
                    <span className={`badge ${STATUS_BADGE[active.status]}`}>
                      {STATUS_LABELS[active.status] || active.status}
                    </span>
                    {(isHrOverview || isInternalControlMember) &&
                    !canEditEntries &&
                    !canApproveActive ? (
                      <span className="badge bg-info text-dark ms-2">
                        للعرض فقط
                      </span>
                    ) : null}
                    {active.status === "APPROVED" &&
                    canEditEntries &&
                    !isCreatorFinalTimesheetScope(active.scope) ? (
                      <span className="badge bg-light text-dark border ms-2">
                        التعديل يحتاج إعادة اعتماد
                      </span>
                    ) : null}
                    {active.status === "APPROVED" &&
                    canEditEntries &&
                    isCreatorFinalTimesheetScope(active.scope) ? (
                      <span className="badge bg-success-subtle text-success border ms-2">
                        معتمدة نهائياً — يمكن التعديل مباشرة
                      </span>
                    ) : null}
                    {isAmendmentDraft ? (
                      <span className="badge bg-warning text-dark ms-2">
                        بانتظار إرسال التعديل للاعتماد
                      </span>
                    ) : null}
                  </div>
                  {active.status === "DRAFT" &&
                  canManageActiveTimesheet &&
                  active.scope !== "INTERNAL_CONTROL" ? (
                    <div className="d-flex gap-2">
                      {!isAmendmentDraft ? (
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={handleDelete}
                          disabled={loading}
                        >
                          حذف المسودة
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleSubmit}
                        disabled={loading}
                      >
                        {isCreatorFinalTimesheetScope(active.scope)
                          ? "اعتماد نهائي"
                          : isAmendmentDraft
                            ? "إرسال التعديل للاعتماد"
                            : "إرسال للاعتماد"}
                      </button>
                    </div>
                  ) : null}
                  {(active.status === "PENDING" ||
                    (active.status === "APPROVED" &&
                      isCreatorFinalTimesheetScope(active.scope))) &&
                  canManageActiveTimesheet ? (
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        {isCreatorFinalTimesheetScope(active.scope)
                          ? "إلغاء الخطة"
                          : "إلغاء الطلب"}
                      </button>
                    </div>
                  ) : null}
                  {canApproveActive &&
                  !isCreatorFinalTimesheetScope(active.scope) ? (
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={handleReject}
                        disabled={loading}
                      >
                        رفض
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleApprove}
                        disabled={loading}
                      >
                        موافقة
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="timesheet-plan-sheet">
                  <div className="timesheet-plan-title">{planTitle}</div>
                  {isHrPlanCreator &&
                  canEditEntries &&
                  active.scope === "BRANCH" ? (
                    <div className="mb-3">
                      <label
                        className="form-label"
                        htmlFor="ts-hr-plan-add-search"
                      >
                        إضافة موظف للخطة — حتى لو مش من الفرع
                      </label>
                      <input
                        id="ts-hr-plan-add-search"
                        type="search"
                        className="form-control"
                        value={hrPlanAddSearch}
                        onChange={(e) => setHrPlanAddSearch(e.target.value)}
                        placeholder="ابحث بالاسم أو الكود..."
                        disabled={loading}
                        autoComplete="off"
                      />
                      {hrPlanAddSearch.trim() ? (
                        hrPlanAddMatches.length ? (
                          <ul className="list-group mt-2">
                            {hrPlanAddMatches.map((emp) => (
                              <li
                                key={emp.id}
                                className="list-group-item d-flex justify-content-between align-items-center gap-2"
                              >
                                <span>
                                  <strong>{emp.fullName || emp.name}</strong>
                                  {emp.employeeCode || emp.code ? (
                                    <span className="text-muted">
                                      {" "}
                                      ({emp.employeeCode || emp.code})
                                    </span>
                                  ) : null}
                                  <div className="small text-muted">
                                    {employeePlaceLabel(emp)}
                                  </div>
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  disabled={loading}
                                  onClick={() =>
                                    handleAddEmployeeToActivePlan(emp)
                                  }
                                >
                                  إضافة
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="form-text">لا توجد نتائج مطابقة</div>
                        )
                      ) : (
                        <div className="form-text">
                          ابحث عن أي موظف عشان تضيفه على الخطة المفتوحة
                        </div>
                      )}
                    </div>
                  ) : null}
                  <div className="timesheet-plan-scroll">
                    <table
                      className={`timesheet-plan-table${
                        active.scope === "INTERNAL_CONTROL" ||
                        active.scope === "WAREHOUSE"
                          ? " timesheet-plan-table--ic"
                          : ""
                      }`}
                    >
                      <thead>
                        <tr>
                          <th className="col-idx">م</th>
                          <th className="col-title">المسمى الوظيفي</th>
                          <th className="col-name">اسم الموظف</th>
                          {activeWeekDates.map((date, idx) => (
                            <th key={date} className="col-day">
                              <div>{DAY_LABELS[idx] || "—"}</div>
                              <div className="timesheet-plan-date">
                                {formatDisplayDate(date)}
                              </div>
                            </th>
                          ))}
                          {canEditEntries &&
                          active.scope !== "INTERNAL_CONTROL" &&
                          active.scope !== "WAREHOUSE" ? (
                            <th className="col-actions">تطبيق</th>
                          ) : null}
                        </tr>
                      </thead>
                      <tbody>
                        {(active.employees || []).map((emp, empIdx) => {
                          const morningDefaults =
                            active.scope === "MODERATOR"
                              ? getModeratorShiftHours("MORNING")
                              : getShiftHours(
                                  "MORNING",
                                  null,
                                  branchShiftHours,
                                );
                          const schedule =
                            schedulesByEmployee[emp.employeeId] ||
                            (emp.shift
                              ? {
                                  shift: emp.shift,
                                  scheduledStartTime:
                                    emp.scheduledStartTime ||
                                    morningDefaults.start,
                                  scheduledEndTime:
                                    emp.scheduledEndTime ||
                                    morningDefaults.end,
                                  workHalfPeriod:
                                    (emp.entries || []).find(
                                      (e) => e.workHalfPeriod,
                                    )?.workHalfPeriod || "",
                                }
                              : active.scope === "MODERATOR"
                                ? defaultModeratorSchedule()
                                : defaultSchedule(branchShiftHours));
                          const jobTitle =
                            emp.jobTitle ||
                            jobTitleByEmployeeId[emp.employeeId] ||
                            "—";
                          const entriesByDate = new Map(
                            (emp.entries || []).map((e) => [e.date, e]),
                          );
                          const coverageNote = emp.isCoverage
                            ? `تغطية من ${emp.homeBranch?.name || "فرع آخر"}`
                            : null;
                          const weekRestCount = countableRestDateKeys(emp).length;
                          const monthKey = String(
                            active.weekStartDate || "",
                          ).slice(0, 7);
                          const monthRestCount = monthRestCountForEmployee(
                            emp,
                            timesheets,
                            active,
                            monthKey,
                          );
                          const monthRestCap = monthlyRestCapForEmployee(
                            emp,
                            monthKey,
                          );
                          const showRestQuota =
                            !emp.isCoverage &&
                            active.scope !== "INTERNAL_CONTROL";

                          return (
                            <tr key={emp.employeeId}>
                              <td className="col-idx cell-meta">{empIdx + 1}</td>
                              <td className="col-title cell-meta">{jobTitle}</td>
                              <td className="col-name cell-meta">
                                <div>{emp.employeeName}</div>
                                {coverageNote ? (
                                  <div className="timesheet-coverage-note">
                                    {coverageNote}
                                  </div>
                                ) : null}
                                {showRestQuota ? (
                                  <div className="timesheet-rest-quota">
                                    راحة {weekRestCount}/{MAX_WEEKLY_REST_DAYS} ·
                                    الشهر {monthRestCount}/{monthRestCap}
                                  </div>
                                ) : null}
                              </td>
                              {activeWeekDates.map((date) => {
                                const entry = entriesByDate.get(date);
                                const kind = getEntryCellKind(entry);
                                const label = getEntryCellLabel(
                                  entry,
                                  active.scope,
                                );
                                const isLeave = entry?.dayType === "LEAVE";
                                const isLockedLeave = Boolean(
                                  isLeave || entry?.leaveHalfPeriod,
                                );
                                const cellValue = encodeCellValue(entry);
                                const restOptionDisabled =
                                  entry?.dayType !== "REST" &&
                                  Boolean(
                                    restQuotaBlockReason(
                                      emp,
                                      date,
                                      timesheets,
                                      active,
                                    ),
                                  );

                                return (
                                  <td
                                    key={`${emp.employeeId}-${date}`}
                                    className={`col-day cell-shift cell-shift--${kind}`}
                                  >
                                    {canEditEntries &&
                                    !isLockedLeave &&
                                    active.scope === "WAREHOUSE" ? (
                                      <div className="timesheet-ic-day-edit">
                                        <select
                                          className="timesheet-plan-select"
                                          value={
                                            entry?.dayType === "REST"
                                              ? "REST"
                                              : "WORK"
                                          }
                                          disabled={loading}
                                          onChange={(e) => {
                                            if (e.target.value === "REST") {
                                              updateWarehouseDay(
                                                emp.employeeId,
                                                date,
                                                { dayType: "REST" },
                                              );
                                              return;
                                            }
                                            updateWarehouseDay(
                                              emp.employeeId,
                                              date,
                                              {
                                                dayType: "WORK",
                                                scheduledStartTime:
                                                  entry?.scheduledStartTime ||
                                                  "09:00",
                                                scheduledEndTime:
                                                  entry?.scheduledEndTime ||
                                                  "19:00",
                                                hasBreak:
                                                  Number(
                                                    entry?.expectedBreakHours,
                                                  ) > 0,
                                              },
                                            );
                                          }}
                                          aria-label={`${emp.employeeName} — ${formatDisplayDate(date)}`}
                                        >
                                          <option value="WORK">عمل</option>
                                          <option
                                            value="REST"
                                            disabled={restOptionDisabled}
                                          >
                                            راحة
                                          </option>
                                        </select>
                                        {entry?.dayType !== "REST" &&
                                        entry?.dayType !== "LEAVE" ? (
                                          <div className="timesheet-ic-day-times">
                                            <div className="d-flex flex-wrap gap-1 mb-1">
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={loading}
                                                onClick={() =>
                                                  updateWarehouseDay(
                                                    emp.employeeId,
                                                    date,
                                                    {
                                                      dayType: "WORK",
                                                      scheduledStartTime:
                                                        warehouseDefaults.start ||
                                                        "09:00",
                                                      scheduledEndTime:
                                                        warehouseDefaults.end ||
                                                        "19:00",
                                                      hasBreak:
                                                        Number(
                                                          entry?.expectedBreakHours,
                                                        ) > 0,
                                                    },
                                                  )
                                                }
                                              >
                                                {`${(warehouseDefaults.start || "09:00").slice(0, 5)}–${(warehouseDefaults.end || "19:00").slice(0, 5)}`}
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={loading}
                                                onClick={() =>
                                                  updateWarehouseDay(
                                                    emp.employeeId,
                                                    date,
                                                    {
                                                      dayType: "WORK",
                                                      scheduledStartTime:
                                                        "09:00",
                                                      scheduledEndTime:
                                                        "18:00",
                                                      hasBreak:
                                                        Number(
                                                          entry?.expectedBreakHours,
                                                        ) > 0,
                                                    },
                                                  )
                                                }
                                              >
                                                9–6
                                              </button>
                                              {customShiftPresets.map(
                                                (preset) => {
                                                  const hours =
                                                    getCustomPresetHours(
                                                      preset,
                                                      date,
                                                    );
                                                  if (!hours) return null;
                                                  return (
                                                    <button
                                                      key={preset.id}
                                                      type="button"
                                                      className="btn btn-sm btn-outline-primary"
                                                      disabled={loading}
                                                      title={`${hours.start}–${hours.end}`}
                                                      onClick={() =>
                                                        updateWarehouseDay(
                                                          emp.employeeId,
                                                          date,
                                                          {
                                                            dayType: "WORK",
                                                            scheduledStartTime:
                                                              hours.start,
                                                            scheduledEndTime:
                                                              hours.end,
                                                            hasBreak:
                                                              Number(
                                                                entry?.expectedBreakHours,
                                                              ) > 0,
                                                          },
                                                        )
                                                      }
                                                    >
                                                      {preset.nameAr}
                                                    </button>
                                                  );
                                                },
                                              )}
                                              <span className="small text-muted align-self-center">
                                                أو اكتب أي من–إلى
                                              </span>
                                            </div>
                                            <HhMmInput
                                              defaultValue={
                                                entry?.scheduledStartTime ||
                                                "09:00"
                                              }
                                              id={`wh-start-${emp.employeeId}-${date}`}
                                              disabled={loading}
                                              aria-label="من"
                                            />
                                            <HhMmInput
                                              defaultValue={
                                                entry?.scheduledEndTime ||
                                                "19:00"
                                              }
                                              id={`wh-end-${emp.employeeId}-${date}`}
                                              disabled={loading}
                                              aria-label="إلى"
                                            />
                                            <label className="form-check small mb-1">
                                              <input
                                                type="checkbox"
                                                className="form-check-input"
                                                defaultChecked={
                                                  Number(
                                                    entry?.expectedBreakHours,
                                                  ) > 0
                                                }
                                                id={`wh-break-${emp.employeeId}-${date}`}
                                                disabled={loading}
                                              />
                                              <span className="form-check-label">
                                                بريك
                                              </span>
                                            </label>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-primary"
                                              disabled={loading}
                                              onClick={() => {
                                                const startEl =
                                                  document.getElementById(
                                                    `wh-start-${emp.employeeId}-${date}`,
                                                  );
                                                const endEl =
                                                  document.getElementById(
                                                    `wh-end-${emp.employeeId}-${date}`,
                                                  );
                                                const breakEl =
                                                  document.getElementById(
                                                    `wh-break-${emp.employeeId}-${date}`,
                                                  );
                                                updateWarehouseDay(
                                                  emp.employeeId,
                                                  date,
                                                  {
                                                    dayType: "WORK",
                                                    scheduledStartTime:
                                                      startEl?.value ||
                                                      "09:00",
                                                    scheduledEndTime:
                                                      endEl?.value || "19:00",
                                                    hasBreak: Boolean(
                                                      breakEl?.checked,
                                                    ),
                                                  },
                                                );
                                              }}
                                            >
                                              حفظ
                                            </button>
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : canEditEntries &&
                                    !isLockedLeave &&
                                    active.scope === "INTERNAL_CONTROL" ? (
                                      <div className="timesheet-ic-day-edit">
                                        <select
                                          className="timesheet-plan-select"
                                          value={
                                            entry?.dayType === "REST"
                                              ? "REST"
                                              : "WORK"
                                          }
                                          disabled={loading}
                                          onChange={(e) => {
                                            if (e.target.value === "REST") {
                                              updateInternalControlDay(
                                                emp.employeeId,
                                                date,
                                                { dayType: "REST" },
                                              );
                                              return;
                                            }
                                            updateInternalControlDay(
                                              emp.employeeId,
                                              date,
                                              {
                                                dayType: "WORK",
                                                scheduledStartTime:
                                                  entry?.scheduledStartTime ||
                                                  "09:00",
                                                scheduledEndTime:
                                                  entry?.scheduledEndTime ||
                                                  "17:00",
                                              },
                                            );
                                          }}
                                          aria-label={`${emp.employeeName} — ${formatDisplayDate(date)}`}
                                        >
                                          <option value="WORK">عمل</option>
                                          <option value="REST">راحة</option>
                                        </select>
                                        {entry?.dayType !== "REST" ? (
                                          <div className="timesheet-ic-day-times">
                                            <HhMmInput
                                              defaultValue={
                                                entry?.scheduledStartTime ||
                                                "09:00"
                                              }
                                              id={`ic-start-${emp.employeeId}-${date}`}
                                              disabled={loading}
                                              aria-label="من"
                                            />
                                            <HhMmInput
                                              defaultValue={
                                                entry?.scheduledEndTime ||
                                                "17:00"
                                              }
                                              id={`ic-end-${emp.employeeId}-${date}`}
                                              disabled={loading}
                                              aria-label="إلى"
                                            />
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-primary"
                                              disabled={loading}
                                              onClick={() => {
                                                const startEl =
                                                  document.getElementById(
                                                    `ic-start-${emp.employeeId}-${date}`,
                                                  );
                                                const endEl =
                                                  document.getElementById(
                                                    `ic-end-${emp.employeeId}-${date}`,
                                                  );
                                                updateInternalControlDay(
                                                  emp.employeeId,
                                                  date,
                                                  {
                                                    dayType: "WORK",
                                                    scheduledStartTime:
                                                      startEl?.value ||
                                                      "09:00",
                                                    scheduledEndTime:
                                                      endEl?.value || "17:00",
                                                  },
                                                );
                                              }}
                                            >
                                              حفظ
                                            </button>
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : canEditEntries && !isLockedLeave ? (
                                      <select
                                        className="timesheet-plan-select"
                                        value={cellValue}
                                        disabled={loading}
                                        onChange={(e) =>
                                          updateDayCell(
                                            emp.employeeId,
                                            date,
                                            e.target.value,
                                            emp,
                                          )
                                        }
                                        aria-label={`${emp.employeeName} — ${formatDisplayDate(date)}`}
                                      >
                                        {active.scope === "MODERATOR" ? (
                                          <>
                                            <option value="NIGHT">00:00–08:00</option>
                                            <option value="MORNING">08:00–16:00</option>
                                            <option value="EVENING">16:00–00:00</option>
                                            <option
                                              value="REST"
                                              disabled={restOptionDisabled}
                                            >
                                              راحة أسبوعية
                                            </option>
                                          </>
                                        ) : (
                                          <>
                                        <option value="MORNING">صباحي</option>
                                        <option value="MORNING:FIRST_HALF">
                                          صباحي نص أول
                                        </option>
                                        <option value="MORNING:SECOND_HALF">
                                          صباحي نص تاني
                                        </option>
                                        <option value="EVENING">مسائي</option>
                                        <option value="EVENING:FIRST_HALF">
                                          مسائي نص أول
                                        </option>
                                        <option value="EVENING:SECOND_HALF">
                                          مسائي نص تاني
                                        </option>
                                        {allowBothShift ? (
                                          <option value="BOTH">يوم كامل</option>
                                        ) : null}
                                        {allowInventoryShift ? (
                                          <option value="INVENTORY">جرد</option>
                                        ) : null}
                                        <option
                                          value="REST"
                                          disabled={restOptionDisabled}
                                        >
                                          راحة أسبوعية
                                        </option>
                                        {(active.scope === "BRANCH" ||
                                          active.scope === "CASHIER") &&
                                        !emp.isCoverage ? (
                                          <option value="COVERAGE">
                                            تغطية (فرع آخر)
                                          </option>
                                        ) : null}
                                          </>
                                        )}
                                      </select>
                                    ) : (
                                      <span>{label}</span>
                                    )}
                                  </td>
                                );
                              })}
                              {canEditEntries &&
                              active.scope !== "INTERNAL_CONTROL" &&
                              active.scope !== "WAREHOUSE" ? (
                                <td className="col-actions cell-meta">
                                  <div className="timesheet-plan-apply">
                                    <select
                                      className="form-select form-select-sm"
                                      value={schedule.shift}
                                      onChange={(e) =>
                                        updateSchedule(emp.employeeId, {
                                          shift: e.target.value,
                                        })
                                      }
                                      disabled={loading}
                                      aria-label="وردية لكل الأيام"
                                    >
                                      {active.scope === "MODERATOR" ? (
                                        <>
                                          <option value="NIGHT">00:00–08:00</option>
                                          <option value="MORNING">08:00–16:00</option>
                                          <option value="EVENING">16:00–00:00</option>
                                        </>
                                      ) : (
                                        <>
                                      <option value="MORNING">صباحي</option>
                                      <option value="EVENING">مسائي</option>
                                      {allowBothShift ? (
                                        <option value="BOTH">يوم كامل</option>
                                      ) : null}
                                      {allowInventoryShift ? (
                                        <option value="INVENTORY">جرد</option>
                                      ) : null}
                                        </>
                                      )}
                                    </select>
                                    {active.scope !== "MODERATOR" &&
                                    schedule.shift !== "INVENTORY" ? (
                                      <select
                                        className="form-select form-select-sm"
                                        value={schedule.workHalfPeriod || ""}
                                        onChange={(e) =>
                                          updateSchedule(emp.employeeId, {
                                            workHalfPeriod: e.target.value,
                                          })
                                        }
                                        disabled={loading}
                                        aria-label="نص الشيفت"
                                      >
                                        <option value="">كامل</option>
                                        <option value="FIRST_HALF">نص أول</option>
                                        <option value="SECOND_HALF">نص تاني</option>
                                      </select>
                                    ) : null}
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      disabled={loading}
                                      onClick={() => applyEmployeeSchedule(emp)}
                                      title="تطبيق على اليوم والأيام الجاية فقط — الأيام السابقة لا تُغيَّر"
                                    >
                                      تطبيق
                                    </button>
                                  </div>
                                </td>
                              ) : null}
                            </tr>
                          );
                        })}
                      </tbody>
                      {active.scope !== "WAREHOUSE" &&
                      active.scope !== "INTERNAL_CONTROL" ? (
                      <tfoot>
                        {active.scope === "MODERATOR" ? (
                          <>
                            <tr className="row-sum row-sum--night">
                              <td colSpan={3}>عدد 00:00–08:00</td>
                              {dayTotals.map((t, i) => (
                                <td key={`n-${i}`}>{t.night}</td>
                              ))}
                              {canEditEntries ? <td /> : null}
                            </tr>
                            <tr className="row-sum row-sum--morning">
                              <td colSpan={3}>عدد 08:00–16:00</td>
                              {dayTotals.map((t, i) => (
                                <td key={`m-${i}`}>{t.morning}</td>
                              ))}
                              {canEditEntries ? <td /> : null}
                            </tr>
                            <tr className="row-sum row-sum--evening">
                              <td colSpan={3}>عدد 16:00–00:00</td>
                              {dayTotals.map((t, i) => (
                                <td key={`e-${i}`}>{t.evening}</td>
                              ))}
                              {canEditEntries ? <td /> : null}
                            </tr>
                          </>
                        ) : (
                          <>
                        <tr className="row-sum row-sum--morning">
                          <td colSpan={3}>عدد الشفت الصباحي</td>
                          {dayTotals.map((t, i) => (
                            <td key={`m-${i}`}>{t.morning}</td>
                          ))}
                          {canEditEntries ? <td /> : null}
                        </tr>
                        <tr className="row-sum row-sum--evening">
                          <td colSpan={3}>عدد الشفت المسائي</td>
                          {dayTotals.map((t, i) => (
                            <td key={`e-${i}`}>{t.evening}</td>
                          ))}
                          {canEditEntries ? <td /> : null}
                        </tr>
                          </>
                        )}
                        <tr className="row-sum row-sum--rest">
                          <td colSpan={3}>راحة أسبوعية</td>
                          {dayTotals.map((t, i) => (
                            <td key={`r-${i}`}>{t.rest}</td>
                          ))}
                          {canEditEntries ? <td /> : null}
                        </tr>
                        {active.scope === "BRANCH" ||
                        active.scope === "CASHIER" ? (
                          <tr className="row-sum row-sum--coverage">
                            <td colSpan={3}>تغطية (فرع آخر)</td>
                            {dayTotals.map((t, i) => (
                              <td key={`c-${i}`}>{t.coverage}</td>
                            ))}
                            {canEditEntries ? <td /> : null}
                          </tr>
                        ) : null}
                        <tr className="row-sum row-sum--strength">
                          <td colSpan={3}>
                            {active.scope === "MODERATOR"
                              ? "اجمالي التغطية"
                              : "اجمالي قوة الفرع"}
                          </td>
                          {dayTotals.map((t, i) => (
                            <td key={`s-${i}`}>{t.strength}</td>
                          ))}
                          {canEditEntries ? <td /> : null}
                        </tr>
                      </tfoot>
                      ) : null}
                    </table>
                  </div>

                  {active.scope !== "WAREHOUSE" &&
                  active.scope !== "INTERNAL_CONTROL" ? (
                  <div className="timesheet-plan-legend" aria-hidden="true">
                    {active.scope === "MODERATOR" ? (
                      <>
                        <span className="legend-item legend-night">00:00–08:00</span>
                        <span className="legend-item legend-morning">08:00–16:00</span>
                        <span className="legend-item legend-evening">16:00–00:00</span>
                      </>
                    ) : (
                      <>
                    <span className="legend-item legend-morning">صباحي</span>
                    <span className="legend-item legend-evening">مسائي</span>
                    {allowBothShift ? (
                      <span className="legend-item legend-full">يوم كامل</span>
                    ) : null}
                    {allowInventoryShift ? (
                      <span className="legend-item legend-inventory">جرد</span>
                    ) : null}
                      </>
                    )}
                    <span className="legend-item legend-rest">راحة أسبوعية</span>
                    {active.scope === "BRANCH" ||
                    active.scope === "CASHIER" ? (
                      <span className="legend-item legend-coverage">
                        تغطية (فرع آخر)
                      </span>
                    ) : null}
                  </div>
                  ) : active.scope === "WAREHOUSE" ? (
                  <div className="timesheet-plan-legend" aria-hidden="true">
                    <span className="legend-item legend-morning">عمل</span>
                    <span className="legend-item legend-rest">راحة</span>
                  </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <div className="alert alert-light border">اختر خطة أسبوعية للعرض</div>
          )}
        </div>
      </div>
    </div>
  );
}
